import React, {
	useState,
	useEffect,
	useMemo,
	useCallback,
	useRef
} from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UploadManagerService } from '../../../services/cloud-file-system/UploadManagerService';
import { MoveManagerService } from '../../../services/cloud-file-system/MoveManagerService';
import {
	getAllBucketUploads,
	addUploadRecords
} from '../../../services/local-relational-database/uploads_idb';
import { envConfig } from '../../../config';
import CloudFileSystemIcon from '../../../assets/cloud-file-system.svg';
import { EditorService } from '../../../services/editor';
import { popupNotification } from '../../../popup-notification';
import Button from '../../../lib/button/Button';
import Loader from '../../../lib/loader/Loader';
import JSZip from 'jszip';
import SmallDialog from '../../../lib/dialog/SmallDialog';
import warningIcon from '../../../assets/warning-sign-24px.png';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers';
import {
	Menu,
	MenuItem,
	IconButton,
	Popover,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	FormControlLabel,
	Checkbox,
	Typography,
	Box,
	Tooltip
} from '@mui/material';
import { getAccessToken } from '../../../auth';
import './cloud-file-system-bucket-explorer.css';

const FILE_CATEGORIES = [
	{
		name: 'Images',
		extensions: [
			'.png',
			'.jpeg',
			'.jpg',
			'.svg',
			'.gif',
			'.webp',
			'.ico',
			'.tiff',
			'.tif',
			'.bmp',
			'.heic',
			'.raw'
		]
	},
	{
		name: 'Videos',
		extensions: ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.wmv', '.flv']
	},
	{
		name: 'Audio',
		extensions: [
			'.mp3',
			'.wav',
			'.ogg',
			'.m4a',
			'.flac',
			'.aac',
			'.midi',
			'.mid'
		]
	},
	{
		name: 'Documents',
		extensions: [
			'.pdf',
			'.doc',
			'.docx',
			'.xls',
			'.xlsx',
			'.ppt',
			'.pptx',
			'.pages',
			'.numbers',
			'.key',
			'.rtf'
		]
	},
	{
		name: 'Plain Text & Code',
		extensions: [
			'.txt',
			'.md',
			'.html',
			'.htm',
			'.css',
			'.js',
			'.ts',
			'.json',
			'.xml',
			'.csv',
			'.yml',
			'.yaml',
			'.py',
			'.sh'
		]
	},
	{
		name: 'Archives & Compressed',
		extensions: ['.zip', '.rar', '.tar', '.gz', '.7z', '.bz2']
	},
	{
		name: 'Executables & System',
		extensions: ['.exe', '.dmg', '.iso', '.apk', '.bin']
	},
	{
		name: 'Design & 3D',
		extensions: [
			'.psd',
			'.ai',
			'.xd',
			'.sketch',
			'.fig',
			'.blend',
			'.obj',
			'.stl'
		]
	},
	{
		name: 'Fonts',
		extensions: ['.ttf', '.otf', '.woff', '.woff2']
	}
];
interface ICloudFileSystemBucketExplorerProps {
	onClose: () => void;
	project: EditorService;
	resourceId: string;
}

export interface IFile {
	key: string;
	size: number;
	lastModified: string;
	url?: string;
}

export const getFileIcon = (filename: string) => {
	const extMatch = filename.match(/\.([^.]+)$/);
	if (!extMatch)
		return {
			className: 'fa-solid fa-file',
			color: '#94a3b8',
			type: 'unknown'
		};

	const ext = extMatch[1].toLowerCase();

	if (
		[
			'png',
			'jpeg',
			'jpg',
			'svg',
			'gif',
			'webp',
			'ico',
			'tiff',
			'tif',
			'bmp',
			'heic',
			'raw'
		].includes(ext)
	) {
		return {
			className: 'fa-regular fa-image',
			color: '#3b82f6',
			type: 'image'
		};
	}
	if (['mp4', 'mov', 'webm', 'avi', 'mkv', 'wmv', 'flv'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-video',
			color: '#ef4444',
			type: 'video'
		};
	}
	if (
		['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac', 'midi', 'mid'].includes(ext)
	) {
		return {
			className: 'fa-regular fa-file-audio',
			color: '#f59e0b',
			type: 'audio'
		};
	}
	if (['pdf'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-pdf',
			color: '#ef4444',
			type: 'document'
		};
	}
	if (['doc', 'docx', 'pages', 'rtf'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-word',
			color: '#2563eb',
			type: 'document'
		};
	}
	if (['xls', 'xlsx', 'csv', 'numbers'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-excel',
			color: '#10b981',
			type: 'document'
		};
	}
	if (['ppt', 'pptx', 'key'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-powerpoint',
			color: '#f97316',
			type: 'document'
		};
	}
	if (
		[
			'html',
			'htm',
			'css',
			'js',
			'ts',
			'json',
			'xml',
			'yml',
			'yaml',
			'py',
			'sh'
		].includes(ext)
	) {
		return {
			className: 'fa-regular fa-file-code',
			color: '#6366f1',
			type: 'code'
		};
	}
	if (['txt', 'md'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-lines',
			color: '#64748b',
			type: 'text'
		};
	}
	if (['zip', 'rar', 'tar', 'gz', '7z', 'bz2'].includes(ext)) {
		return {
			className: 'fa-regular fa-file-zipper',
			color: '#8b5cf6',
			type: 'archive'
		};
	}
	if (['exe', 'dmg', 'iso', 'apk', 'bin'].includes(ext)) {
		return {
			className: 'fa-solid fa-gear',
			color: '#64748b',
			type: 'executable'
		};
	}
	if (
		['psd', 'ai', 'xd', 'sketch', 'fig', 'blend', 'obj', 'stl'].includes(
			ext
		)
	) {
		return {
			className: 'fa-solid fa-pen-ruler',
			color: '#ec4899',
			type: 'design'
		};
	}
	if (['ttf', 'otf', 'woff', 'woff2'].includes(ext)) {
		return {
			className: 'fa-solid fa-font',
			color: '#14b8a6',
			type: 'font'
		};
	}

	return {
		className: 'fa-regular fa-file',
		color: '#94a3b8',
		type: 'unknown'
	};
};

export const formatBytes = (bytes: number) => {
	if (bytes === 0) return '0 Bytes';
	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const ImageThumbnail = ({
	file,
	project,
	resourceId
}: {
	file: IFile;
	project: any;
	resourceId: string;
}) => {
	const [src, setSrc] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		const fetchImage = async () => {
			try {
				const token = await getAccessToken();
				const url = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(file.key)}`;
				const response = await fetch(url, {
					headers: { Authorization: `Bearer ${token}` }
				});
				if (response.ok) {
					const blob = await response.blob();
					if (isMounted) setSrc(URL.createObjectURL(blob));
				}
			} catch (e) {
				console.error('Failed to load thumbnail', e);
			}
		};
		fetchImage();
		return () => {
			isMounted = false;
			if (src) URL.revokeObjectURL(src);
		};
	}, [file.key]);

	if (!src)
		return (
			<div
				style={{
					height: '48px',
					width: '48px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					marginBottom: '12px',
					background: '#f1f5f9',
					borderRadius: '4px'
				}}
			>
				<Loader size={20} />
			</div>
		);
	return (
		<img
			src={src}
			alt={file.key}
			style={{
				maxHeight: '48px',
				maxWidth: '100%',
				objectFit: 'contain',
				marginBottom: '12px',
				borderRadius: '4px'
			}}
		/>
	);
};

const CloudFileSystemBucketExplorer: React.FC<
	ICloudFileSystemBucketExplorerProps
> = ({ onClose, project, resourceId }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();

	const path = searchParams.get('path') || '';
	const viewMode = searchParams.get('view') || 'tree'; // 'tree', 'list'
	const layoutMode = searchParams.get('layout') || 'table'; // 'table', 'grid'
	const searchQuery = searchParams.get('search') || '';
	const typeFilter = (searchParams.get('type') || '')
		.split(',')
		.filter(Boolean);
	const dateFrom = searchParams.get('dateFrom') || '';
	const dateTo = searchParams.get('dateTo') || '';
	const accessFilter = searchParams.get('access') || '';

	useEffect(() => {
		const viewFile = searchParams.get('viewFile');
		if (viewFile) {
			navigate(`view/${encodeURIComponent(viewFile)}`, { replace: true });
		}
	}, [searchParams, navigate]);

	const [files, setFiles] = useState<IFile[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
	const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
	const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
	const [newFolderName, setNewFolderName] = useState('');
	const [deleteConfirmFile, setDeleteConfirmFile] = useState<IFile | null>(
		null
	);
	const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

	const [filterAnchorEl, setFilterAnchorEl] =
		useState<HTMLButtonElement | null>(null);

	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [menuFile, setMenuFile] = useState<IFile | null>(null);

	const handleOpenMenu = (
		event: React.MouseEvent<HTMLElement>,
		file: IFile
	) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setMenuFile(file);
	};

	const handleCloseMenu = (e?: React.MouseEvent) => {
		if (e) e.stopPropagation();
		setMenuAnchorEl(null);
		setMenuFile(null);
	};

	const fileInputRef = useRef<HTMLInputElement>(null);
	const folderInputRef = useRef<HTMLInputElement>(null);
	const [uploadMenuAnchorEl, setUploadMenuAnchorEl] =
		useState<null | HTMLElement>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState({
		total: 0,
		completed: 0
	});

	const [dragActive, setDragActive] = useState(false);
	const [largeFileUploads, setLargeFileUploads] = useState<
		Record<string, { completed: number; total: number }>
	>({});
	const [cancellingInfo, setCancellingInfo] = useState<{
		isCancelling: boolean;
		deletingCount: number;
	} | null>(null);

	const [isMoving, setIsMoving] = useState(false);
	const [moveProgress, setMoveProgress] = useState({
		total: 0,
		completed: 0
	});
	const [cancellingMoveInfo, setCancellingMoveInfo] = useState<{
		isCancelling: boolean;
	} | null>(null);
	const maxMoveBatchTotalRef = useRef<number>(0);
	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadingKeys, setDownloadingKeys] = useState<Set<string>>(
		new Set()
	);
	const [conflictState, setConflictState] = useState<{
		filesToUpload: { file: File; key: string }[];
		resolutions: Record<string, 'replace' | 'keep_both' | 'skip'>;
	} | null>(null);
	const [operatingKeys, setOperatingKeys] = useState<Set<string>>(new Set());
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
	const [renameTargetFile, setRenameTargetFile] = useState<IFile | null>(
		null
	);
	const [renameNewName, setRenameNewName] = useState('');
	const [isRenaming, setIsRenaming] = useState(false);
	const [isUpdatingBulkAccess, setIsUpdatingBulkAccess] = useState(false);

	const [isCopyingBatch, setIsCopyingBatch] = useState(false);
	const [copyProgress, setCopyProgress] = useState({
		total: 0,
		completed: 0,
		title: ''
	});

	const [isMoveConflictModalOpen, setIsMoveConflictModalOpen] =
		useState(false);
	const [pendingMove, setPendingMove] = useState<{
		sourceItems: IFile[];
		destinationFolderPath: string;
	} | null>(null);

	const [draggedItem, setDraggedItem] = useState<IFile | null>(null);
	const [draggedItems, setDraggedItems] = useState<IFile[]>([]);
	const [dragHoverTargetKey, setDragHoverTargetKey] = useState<string | null>(
		null
	);
	const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);
	const wasDraggingRef = useRef<boolean>(false);

	// Pressing Escape unselects all items
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setSelectedKeys(new Set());
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	const generateUniqueKey = (
		key: string,
		existingKeys: Set<string>
	): string => {
		const lastSlashIndex = key.lastIndexOf('/');
		const dir =
			lastSlashIndex !== -1 ? key.substring(0, lastSlashIndex + 1) : '';
		const filename =
			lastSlashIndex !== -1 ? key.substring(lastSlashIndex + 1) : key;
		const lastDotIndex = filename.lastIndexOf('.');
		const baseName =
			lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
		const ext = lastDotIndex > 0 ? filename.substring(lastDotIndex) : '';

		let counter = 1;
		let newKey = `${dir}${baseName} (${counter})${ext}`;
		while (existingKeys.has(newKey)) {
			counter++;
			newKey = `${dir}${baseName} (${counter})${ext}`;
		}
		return newKey;
	};

	const handleCancelBatchUpload = async () => {
		const active =
			UploadManagerService.getActiveUploadsForBucket(resourceId);
		const keysToCancel = active.map((a) => a.key);
		const deletingCount = active.filter(
			(a) => a.completedParts > 0 || a.status === 'completed'
		).length;

		setCancellingInfo({ isCancelling: true, deletingCount });

		try {
			for (const u of active) {
				await UploadManagerService.cancelUpload(
					u.id,
					getAccessToken,
					envConfig
				);
			}
			if (keysToCancel.length > 0) {
				try {
					await axios.delete(
						`${envConfig.API_BASE_URL}/cloud-files-service/v1/files`,
						{
							headers: {
								Authorization: `Bearer ${await getAccessToken()}`
							},
							data: {
								keys: keysToCancel,
								bucket: resourceId
							}
						}
					);
				} catch (e) {
					// Ignore if files were already cleaned up
				}
			}
			popupNotification({
				text: 'Upload batch cancelled and files cleaned up',
				color: 'error'
			});
		} catch (err) {
			console.error('Error cancelling upload batch:', err);
		} finally {
			setCancellingInfo(null);
			setIsUploading(false);
			setLargeFileUploads({});
			fetchFiles();
		}
	};

	const handleCancelBatchMove = async () => {
		setCancellingMoveInfo({ isCancelling: true });
		try {
			const active =
				MoveManagerService.getActiveMovesForBucket(resourceId);
			if (active.length > 0) {
				const batchId = active[0].batchId;
				await MoveManagerService.cancelBatch(batchId);
			}
			popupNotification({
				text: 'Move batch cancelled',
				color: 'error'
			});
		} catch (err) {
			console.error('Error cancelling move batch:', err);
		} finally {
			setCancellingMoveInfo(null);
			setIsMoving(false);
			maxMoveBatchTotalRef.current = 0;
			setMoveProgress({ total: 0, completed: 0 });
			fetchFiles();
		}
	};

	// Get bucket entity config
	const bucketEntity = useMemo(() => {
		return project.liveResources?.find((r) => r.resourceId === resourceId);
	}, [project.lastPublishedProjectState, resourceId]);

	const isBucketPublic = useMemo(() => {
		const bucketAccessRaw =
			(bucketEntity?.details as any)?.access ||
			((bucketEntity?.details as any)?.publicReadAccess
				? 'public-read'
				: 'private');

		return (
			bucketAccessRaw.toLowerCase() === 'public' ||
			bucketAccessRaw.toLowerCase() === 'public-read'
		);
	}, [bucketEntity]);

	const oppositeAccessFilesCount = useMemo(() => {
		return files.filter((file) => {
			if (file.key.endsWith('/')) return false;
			const raw = (file as any).access;
			if (!raw) return false;
			const isFilePublic =
				raw.toLowerCase() === 'public' ||
				raw.toLowerCase() === 'public-read';
			return isBucketPublic ? !isFilePublic : isFilePublic;
		}).length;
	}, [files, isBucketPublic]);

	const fetchFiles = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axios.get(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files?bucket=${resourceId}`,
				{
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					}
				}
			);
			if (currentBucketIdRef.current === resourceId) {
				setFiles(response.data.items || []);
			}
		} catch (error) {
			console.error('Error fetching bucket files:', error);
			if (currentBucketIdRef.current === resourceId) {
				popupNotification({
					text: 'Failed to load files',
					color: 'error'
				});
			}
		} finally {
			if (currentBucketIdRef.current === resourceId) {
				setIsLoading(false);
			}
		}
	}, [resourceId, getAccessToken]);

	useEffect(() => {
		if (resourceId) {
			setFiles([]);
			setIsLoading(true);
			setSelectedKeys(new Set());
			fetchFiles();
		}
	}, [resourceId, fetchFiles]);

	const currentBucketIdRef = useRef<string>(resourceId);
	useEffect(() => {
		currentBucketIdRef.current = resourceId;
	}, [resourceId]);

	const maxBatchTotalRef = useRef<number>(0);

	// Sync with active background / resumed uploads from UploadManagerService
	useEffect(() => {
		// Reset upload UI state when switching buckets
		maxBatchTotalRef.current = 0;
		setIsUploading(false);
		setUploadProgress({ total: 0, completed: 0 });
		setLargeFileUploads({});

		const syncActiveUploads = async () => {
			if (currentBucketIdRef.current !== resourceId) return;

			const active =
				UploadManagerService.getActiveUploadsForBucket(resourceId);

			if (active.length === 0) {
				maxBatchTotalRef.current = 0;
				setIsUploading(false);
				setUploadProgress({ total: 0, completed: 0 });
				setLargeFileUploads({});
				return;
			}

			let totalFiles = active.length;
			let completedFiles = 0;

			try {
				const allRecords = await getAllBucketUploads(resourceId);
				if (allRecords.length > 0) {
					totalFiles = allRecords.length;
					completedFiles = allRecords.filter(
						(r) => r.status === 'completed'
					).length;
				} else {
					if (active.length > maxBatchTotalRef.current) {
						maxBatchTotalRef.current = active.length;
					}
					totalFiles = maxBatchTotalRef.current;
					completedFiles = totalFiles - active.length;
				}
			} catch (e) {
				if (active.length > maxBatchTotalRef.current) {
					maxBatchTotalRef.current = active.length;
				}
				totalFiles = maxBatchTotalRef.current;
				completedFiles = totalFiles - active.length;
			}

			const largeMap: Record<
				string,
				{ completed: number; total: number }
			> = {};

			for (const u of active) {
				if (u.fileSize > 50 * 1024 * 1024) {
					largeMap[u.fileName] = {
						completed: u.completedParts,
						total: u.totalParts
					};
				}
			}

			if (currentBucketIdRef.current !== resourceId) return;

			setIsUploading(true);
			setUploadProgress((prev) => {
				const total = Math.max(prev.total, totalFiles);
				const completed =
					prev.total > totalFiles ? prev.completed : completedFiles;
				if (prev.total === total && prev.completed === completed) {
					return prev;
				}
				return { total, completed };
			});
			setLargeFileUploads((prev) => {
				const isSame =
					Object.keys(prev).length === Object.keys(largeMap).length &&
					Object.entries(largeMap).every(
						([k, v]) =>
							prev[k] &&
							prev[k].completed === v.completed &&
							prev[k].total === v.total
					);
				return isSame ? prev : { ...prev, ...largeMap };
			});
		};

		syncActiveUploads();
		const unsubscribe = UploadManagerService.subscribe(() => {
			if (currentBucketIdRef.current !== resourceId) return;

			const active =
				UploadManagerService.getActiveUploadsForBucket(resourceId);

			if (active.length > 0) {
				syncActiveUploads();
			} else if (maxBatchTotalRef.current > 0) {
				maxBatchTotalRef.current = 0;
				setIsUploading(false);
				setUploadProgress({ total: 0, completed: 0 });
				setLargeFileUploads({});
				fetchFiles();
			}
		});

		const syncActiveMoves = () => {
			const activeMoves =
				MoveManagerService.getActiveMovesForBucket(resourceId);
			const batchProgress =
				MoveManagerService.getBatchProgressForBucket(resourceId);

			if (batchProgress) {
				setIsMoving(true);
				setMoveProgress({
					total: batchProgress.total,
					completed: batchProgress.completed
				});
				setOperatingKeys((prev) => {
					const next = new Set(prev);
					activeMoves.forEach((m) => next.add(m.sourceKey));
					return next;
				});
			} else if (activeMoves.length > 0) {
				setIsMoving(true);
				setOperatingKeys((prev) => {
					const next = new Set(prev);
					activeMoves.forEach((m) => next.add(m.sourceKey));
					return next;
				});
			}
		};

		syncActiveMoves();
		const unsubscribeMove = MoveManagerService.subscribe(() => {
			if (currentBucketIdRef.current !== resourceId) return;

			const active =
				MoveManagerService.getActiveMovesForBucket(resourceId);
			const batchProgress =
				MoveManagerService.getBatchProgressForBucket(resourceId);

			if (batchProgress || active.length > 0) {
				syncActiveMoves();
			} else {
				maxMoveBatchTotalRef.current = 0;
				setIsMoving(false);
				setCancellingMoveInfo(null);
				setMoveProgress({ total: 0, completed: 0 });
				fetchFiles();
			}
		});

		return () => {
			unsubscribe();
			unsubscribeMove();
		};
	}, [project.projectId, resourceId, fetchFiles]);

	const handleDrag = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();

		// Only show global upload dropzone for external OS file drags, not internal item move drags
		const isExternalFileDrag =
			!draggedItem &&
			e.dataTransfer.types &&
			Array.from(e.dataTransfer.types).includes('Files');

		if (!isExternalFileDrag) {
			return;
		}

		if (e.type === 'dragenter' || e.type === 'dragover') {
			setDragActive(true);
		} else if (e.type === 'dragleave') {
			setDragActive(false);
		}
	};

	const handleDrop = async (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setDragActive(false);

		// If this was an internal move drag drop, ignore global upload handler
		if (draggedItem) {
			return;
		}

		const items = e.dataTransfer.items;
		if (items) {
			const filesWithPaths: { file: File; key: string }[] = [];

			const traverseFileTree = async (
				item: any,
				currentPath: string = ''
			) => {
				if (item.isFile) {
					const file = await new Promise<File>((resolve) =>
						item.file(resolve)
					);
					filesWithPaths.push({ file, key: currentPath + file.name });
				} else if (item.isDirectory) {
					const dirReader = item.createReader();
					const entries = await new Promise<any[]>((resolve) =>
						dirReader.readEntries(resolve)
					);
					for (const entry of entries) {
						await traverseFileTree(
							entry,
							currentPath + item.name + '/'
						);
					}
				}
			};

			const promises = [];
			for (let i = 0; i < items.length; i++) {
				const item = items[i].webkitGetAsEntry();
				if (item) {
					promises.push(traverseFileTree(item, path));
				}
			}

			await Promise.all(promises);
			if (filesWithPaths.length > 0) {
				await handleUpload(filesWithPaths);
			}
		} else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const filesWithPaths = Array.from(e.dataTransfer.files).map(
				(file) => ({
					file,
					key: path + file.name
				})
			);
			await handleUpload(filesWithPaths);
		}
	};

	const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const filesWithPaths = Array.from(e.target.files).map((file) => ({
				file,
				key: path + (file.webkitRelativePath || file.name)
			}));
			handleUpload(filesWithPaths);
			e.target.value = '';
		}
	};

	const handleUpload = async (
		filesToUpload: { file: File; key: string }[]
	) => {
		const existingKeysSet = new Set(files.map((f) => f.key));
		const conflictingItems = filesToUpload.filter((item) =>
			existingKeysSet.has(item.key)
		);

		if (conflictingItems.length > 0) {
			const initialResolutions: Record<
				string,
				'replace' | 'keep_both' | 'skip'
			> = {};
			for (const c of conflictingItems) {
				initialResolutions[c.key] = 'replace';
			}
			setConflictState({
				filesToUpload,
				resolutions: initialResolutions
			});
			return;
		}

		await startBatchUpload(filesToUpload);
	};

	const handleProceedWithResolutions = async () => {
		if (!conflictState) return;

		const existingKeysSet = new Set(files.map((f) => f.key));
		const resolvedFiles: { file: File; key: string }[] = [];
		const usedKeysInBatch = new Set(existingKeysSet);

		for (const item of conflictState.filesToUpload) {
			const res = conflictState.resolutions[item.key] || 'replace';
			if (res === 'skip') {
				continue;
			} else if (res === 'keep_both') {
				const newKey = generateUniqueKey(item.key, usedKeysInBatch);
				usedKeysInBatch.add(newKey);
				resolvedFiles.push({ file: item.file, key: newKey });
			} else {
				usedKeysInBatch.add(item.key);
				resolvedFiles.push(item);
			}
		}

		setConflictState(null);
		await startBatchUpload(resolvedFiles);
	};

	const startBatchUpload = async (
		filesToUpload: { file: File; key: string }[]
	) => {
		if (filesToUpload.length === 0) return;

		const targetBucketId = resourceId;

		if (currentBucketIdRef.current === targetBucketId) {
			setIsUploading(true);
			setUploadProgress({ total: filesToUpload.length, completed: 0 });
		}

		const batchId = `batch_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
		let batchRecords: any[] = [];
		try {
			batchRecords = await addUploadRecords(
				filesToUpload.map(({ file, key }) => ({
					batchId,
					bucket: targetBucketId,
					key,
					file,
					parts: [] as { ETag: string; PartNumber: number }[],
					status: 'pending',
					createdAt: Date.now(),
					updatedAt: Date.now()
				}))
			);
		} catch (e) {
			console.error(
				'Failed to pre-register upload batch records in IDB',
				e
			);
		}

		let successCount = 0;
		const newFiles = [...files];

		try {
			for (let i = 0; i < filesToUpload.length; i++) {
				const { file, key } = filesToUpload[i];
				const record = batchRecords[i];

				// Handle large files with Multipart Upload logic
				if (file.size > 50 * 1024 * 1024) {
					if (currentBucketIdRef.current === targetBucketId) {
						setLargeFileUploads((prev) => ({
							...prev,
							[file.name]: { completed: 0, total: 1 }
						}));
					}
				}

				try {
					await UploadManagerService.uploadFile({
						file,
						key,
						bucket: targetBucketId,
						batchId,
						record,
						getAccessToken,
						envConfig,
						onProgress: (completed, total) => {
							if (currentBucketIdRef.current !== targetBucketId)
								return;
							if (file.size > 50 * 1024 * 1024) {
								setLargeFileUploads((prev) => ({
									...prev,
									[file.name]: { completed, total }
								}));
							}
						}
					});
				} catch (fileErr) {
					console.error(`Failed to upload file ${key}`, fileErr);

					if (file.size > 50 * 1024 * 1024) {
						if (currentBucketIdRef.current === targetBucketId) {
							setLargeFileUploads((prev) => {
								const next = { ...prev };
								delete next[file.name];
								return next;
							});
						}
					}

					throw fileErr;
				}

				if (file.size > 50 * 1024 * 1024) {
					if (currentBucketIdRef.current === targetBucketId) {
						setLargeFileUploads((prev) => {
							const next = { ...prev };
							delete next[file.name];
							return next;
						});
					}
				}

				successCount++;

				if (isBucketPublic) {
					try {
						const token = await getAccessToken();
						await axios.post(
							`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/access`,
							{
								bucket: targetBucketId,
								key,
								access: 'public-read'
							},
							{
								headers: { Authorization: `Bearer ${token}` }
							}
						);
					} catch (e) {
						console.warn(
							'Failed auto-setting public access for uploaded file:',
							key,
							e
						);
					}
				}

				if (currentBucketIdRef.current === targetBucketId) {
					setUploadProgress((prev) => ({
						...prev,
						completed: successCount
					}));

					const existingIndex = newFiles.findIndex(
						(f) => f.key === key
					);
					const updatedFile: any = {
						key,
						size: file.size,
						lastModified: new Date().toISOString(),
						access: isBucketPublic ? 'public-read' : 'private'
					};
					if (existingIndex >= 0) {
						newFiles[existingIndex] = updatedFile;
					} else {
						newFiles.push(updatedFile);
					}
					setFiles([...newFiles]);
				}
			}

			if (currentBucketIdRef.current === targetBucketId) {
				popupNotification({
					text: `Successfully uploaded ${successCount} file(s)`,
					color: 'success'
				});
			}
		} catch (error) {
			console.error('Error in batch upload:', error);
		} finally {
			if (currentBucketIdRef.current === targetBucketId) {
				setIsUploading(false);
				setTimeout(
					() => setUploadProgress({ total: 0, completed: 0 }),
					1000
				);
				fetchFiles();
			}
		}
	};

	const handleDelete = async (key: string) => {
		try {
			// Expand folder keys to all their contents
			const keysToDelete = files
				.filter((f) => f.key.startsWith(key))
				.map((f) => f.key);

			if (!keysToDelete.includes(key)) {
				keysToDelete.push(key);
			}

			await axios.delete(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files`,
				{
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					},
					data: {
						bucket: resourceId,
						keys: keysToDelete
					}
				}
			);

			popupNotification({ text: 'Item deleted', color: 'success' });
			setFiles((prev) => prev.filter((f) => !f.key.startsWith(key)));
			setSelectedKeys((prev) => {
				const next = new Set(prev);
				for (const sk of next) {
					if (sk.startsWith(key)) {
						next.delete(sk);
					}
				}
				return next;
			});
		} catch (error) {
			console.error('Error deleting file:', error);
			popupNotification({
				text: 'Failed to delete file',
				color: 'error'
			});
		}
	};

	const expandedSelectedFiles = useMemo(() => {
		const result = new Set<IFile>();
		for (const selectedKey of selectedKeys) {
			files.forEach((f) => {
				if (f.key === selectedKey || f.key.startsWith(selectedKey)) {
					result.add(f);
				}
			});
		}
		return Array.from(result);
	}, [selectedKeys, files]);

	const confirmBulkDelete = async () => {
		if (expandedSelectedFiles.length === 0) return;
		setIsBulkDeleteDialogOpen(false);

		try {
			const keysToDelete = expandedSelectedFiles.map((f) => f.key);
			// Always include the original selected keys just in case (e.g. empty synthetic folder, though unlikely to exist on backend)
			for (const sk of selectedKeys) {
				if (!keysToDelete.includes(sk)) keysToDelete.push(sk);
			}

			await axios.delete(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files`,
				{
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					},
					data: {
						bucket: resourceId,
						keys: keysToDelete
					}
				}
			);
			popupNotification({
				text: `Deleted ${expandedSelectedFiles.length} files`,
				color: 'success'
			});
			setFiles((prev) =>
				prev.filter((f) => {
					for (const key of selectedKeys) {
						if (f.key.startsWith(key)) return false;
					}
					return true;
				})
			);
			setSelectedKeys(new Set());
		} catch (error) {
			console.error('Error bulk deleting files:', error);
			popupNotification({
				text: 'Failed to delete selected files',
				color: 'error'
			});
		}
	};

	const handleBulkDownload = async () => {
		if (expandedSelectedFiles.length === 0 || isDownloading) return;
		setIsDownloading(true);
		popupNotification({ text: 'Starting download...', color: 'primary' });
		const zip = new JSZip();

		try {
			for (const file of expandedSelectedFiles) {
				const rawUrl = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(file.key)}`;
				const resp = await axios.get(rawUrl, {
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					},
					responseType: 'blob'
				});
				zip.file(file.key, resp.data);
			}
			const content = await zip.generateAsync({ type: 'blob' });
			const link = document.createElement('a');
			link.href = URL.createObjectURL(content);
			link.download = 'download.zip';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			popupNotification({ text: 'Download ready', color: 'success' });
		} catch (error) {
			console.error('Error downloading files:', error);
			popupNotification({
				text: 'Failed to download files',
				color: 'error'
			});
		} finally {
			setIsDownloading(false);
		}
	};

	const handleDownload = async (file: IFile) => {
		if (downloadingKeys.has(file.key)) return;
		setDownloadingKeys((prev) => new Set(prev).add(file.key));
		const fileName =
			(file as any).name ||
			file.key.split('/').filter(Boolean).pop() ||
			'download';

		const isFolder = (file as any).isFolder || file.key.endsWith('/');

		try {
			if (isFolder) {
				popupNotification({
					text: `Zipping folder "${fileName}"...`,
					color: 'primary'
				});
				const folderFiles = files.filter(
					(f) =>
						!(f as any).isFolder &&
						!f.key.endsWith('/') &&
						f.key.startsWith(file.key)
				);

				if (folderFiles.length === 0) {
					popupNotification({
						text: `Folder "${fileName}" is empty`,
						color: 'warning'
					});
					return;
				}

				const zip = new JSZip();
				for (const f of folderFiles) {
					const relativePathInFolder = f.key.substring(
						file.key.length
					);
					const rawUrl = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(f.key)}`;
					const resp = await axios.get(rawUrl, {
						headers: {
							Authorization: `Bearer ${await getAccessToken()}`
						},
						responseType: 'blob'
					});
					zip.file(
						relativePathInFolder ||
							f.key.split('/').pop() ||
							'file',
						resp.data
					);
				}
				const content = await zip.generateAsync({ type: 'blob' });
				const link = document.createElement('a');
				link.href = URL.createObjectURL(content);
				link.download = `${fileName}.zip`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				popupNotification({
					text: `Folder "${fileName}" downloaded`,
					color: 'success'
				});
			} else {
				popupNotification({
					text: `Downloading "${fileName}"...`,
					color: 'primary'
				});
				const rawUrl = `${envConfig.API_BASE_URL}/cloud-files-service/v1/files/download?bucket=${resourceId}&key=${encodeURIComponent(file.key)}`;
				const resp = await axios.get(rawUrl, {
					headers: {
						Authorization: `Bearer ${await getAccessToken()}`
					},
					responseType: 'blob'
				});
				const link = document.createElement('a');
				link.href = URL.createObjectURL(resp.data);
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				popupNotification({
					text: `Downloaded "${fileName}"`,
					color: 'success'
				});
			}
		} catch (error) {
			console.error('Error downloading:', error);
			popupNotification({
				text: `Failed to download "${fileName}"`,
				color: 'error'
			});
		} finally {
			setDownloadingKeys((prev) => {
				const next = new Set(prev);
				next.delete(file.key);
				return next;
			});
		}
	};

	const folderValidationError = useMemo(() => {
		if (!newFolderName) return null;

		const trimmed = newFolderName.trim();
		if (!trimmed) return 'Folder name cannot be empty.';

		const rawSegments = trimmed
			.replace(/^\/+/, '')
			.replace(/\/+$/, '')
			.split('/');

		for (const seg of rawSegments) {
			const s = seg.trim();
			if (!s) return 'Folder path contains empty path segments.';
			if (/[\\:*?"<>|]/.test(s)) {
				return 'Folder name contains invalid characters (\\ : * ? " < > |).';
			}
		}

		const cleanedPath = rawSegments.map((s) => s.trim()).join('/');
		const targetFolderKey = path + cleanedPath + '/';
		const targetFileKey = path + cleanedPath;

		const exists = files.some((f) => {
			if (!f.key) return false;
			if (f.key === targetFolderKey || f.key === targetFileKey)
				return true;
			if (f.key.startsWith(targetFolderKey)) return true;
			const normalizedKey = f.key.endsWith('/') ? f.key : f.key + '/';
			return normalizedKey === targetFolderKey;
		});

		if (exists) {
			return `A folder or file named "${cleanedPath}" already exists.`;
		}

		return null;
	}, [newFolderName, path, files]);

	const handleCreateFolder = async () => {
		if (!newFolderName || folderValidationError) return;
		const rawSegments = newFolderName
			.trim()
			.replace(/^\/+/, '')
			.replace(/\/+$/, '')
			.split('/');
		const cleanedPath = rawSegments.map((s) => s.trim()).join('/');
		const folderKey = path + cleanedPath + '/';
		try {
			const token = await getAccessToken();
			await axios.post(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/folders`,
				{
					bucket: resourceId,
					folderPath: folderKey
				},
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			);
			if (isBucketPublic) {
				try {
					await axios.post(
						`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/access`,
						{
							bucket: resourceId,
							key: folderKey,
							access: 'public-read'
						},
						{
							headers: { Authorization: `Bearer ${token}` }
						}
					);
				} catch (e) {
					console.warn(
						'Failed auto-setting public access for created folder:',
						folderKey,
						e
					);
				}
			}
			popupNotification({
				text: `Folder "${cleanedPath}" created`,
				color: 'success'
			});
			setIsFolderDialogOpen(false);
			setNewFolderName('');
			fetchFiles();
		} catch (err: any) {
			console.error('Error creating folder:', err);
			popupNotification({
				text: 'Failed to create folder',
				color: 'error'
			});
		}
	};

	const handleCopyItem = async (file: IFile) => {
		const isFolder = (file as any).isFolder || file.key.endsWith('/');
		setOperatingKeys((prev) => new Set(prev).add(file.key));

		try {
			const token = await getAccessToken();

			if (!isFolder) {
				const targetKey = generateUniqueKey(
					file.key,
					new Set(files.map((f) => f.key))
				);
				await axios.post(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/copy`,
					{
						bucket: resourceId,
						destinationBucket: resourceId,
						sourceKey: file.key,
						destinationKey: targetKey
					},
					{
						headers: { Authorization: `Bearer ${token}` }
					}
				);
				popupNotification({
					text: `Cloned "${file.key.split('/').pop()}"`,
					color: 'success'
				});
				fetchFiles();
			} else {
				const sourceFolderPrefix = file.key.endsWith('/')
					? file.key
					: file.key + '/';
				const folderBasename =
					sourceFolderPrefix.slice(0, -1).split('/').pop() ||
					'folder';
				const parentPath = sourceFolderPrefix.substring(
					0,
					sourceFolderPrefix.length - folderBasename.length - 1
				);

				const existingFolderKeys = new Set(
					files.map((f) =>
						f.key.endsWith('/') ? f.key.slice(0, -1) : f.key
					)
				);
				const targetFolderName = generateUniqueKey(
					parentPath + folderBasename,
					existingFolderKeys
				)
					.split('/')
					.pop()!;

				const destinationFolderPrefix =
					parentPath + targetFolderName + '/';

				const nestedFilesCount = files.filter(
					(f) =>
						f.key.startsWith(sourceFolderPrefix) &&
						f.key !== sourceFolderPrefix &&
						!f.key.endsWith('/')
				).length;

				const itemCountText =
					nestedFilesCount > 0
						? ` (${nestedFilesCount} ${nestedFilesCount === 1 ? 'item' : 'items'})`
						: '';

				setIsCopyingBatch(true);
				setCopyProgress({
					total: 1,
					completed: 0,
					title: `Cloning folder "${folderBasename}"${itemCountText} as "${targetFolderName}"...`
				});

				await axios.post(
					`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/copy`,
					{
						bucket: resourceId,
						destinationBucket: resourceId,
						sourceKey: sourceFolderPrefix,
						destinationKey: destinationFolderPrefix
					},
					{
						headers: { Authorization: `Bearer ${token}` }
					}
				);

				popupNotification({
					text: `Cloned folder "${folderBasename}"${itemCountText} as "${targetFolderName}"`,
					color: 'success'
				});
				fetchFiles();
			}
		} catch (err: any) {
			console.error('Error cloning item:', err);
			popupNotification({ text: 'Failed to clone item', color: 'error' });
		} finally {
			setIsCopyingBatch(false);
			setCopyProgress({ total: 0, completed: 0, title: '' });
			setOperatingKeys((prev) => {
				const next = new Set(prev);
				next.delete(file.key);
				return next;
			});
		}
	};

	const handleBulkClone = async () => {
		if (selectedKeys.size === 0) return;
		const selectedItems = currentViewItems.filter((f) =>
			selectedKeys.has(f.key)
		);
		if (selectedItems.length === 0) return;

		const totalItemsToClone =
			expandedSelectedFiles.length || selectedItems.length;

		setIsCopyingBatch(true);
		setCopyProgress({
			total: totalItemsToClone,
			completed: 0,
			title: `Cloning ${totalItemsToClone} ${totalItemsToClone === 1 ? 'item' : 'items'}...`
		});

		let completedCount = 0;
		const CHUNK_SIZE = 5;

		try {
			const token = await getAccessToken();
			for (let i = 0; i < selectedItems.length; i += CHUNK_SIZE) {
				const chunk = selectedItems.slice(i, i + CHUNK_SIZE);
				await Promise.all(
					chunk.map(async (file) => {
						const isFolder =
							(file as any).isFolder || file.key.endsWith('/');
						if (!isFolder) {
							const targetKey = generateUniqueKey(
								file.key,
								new Set(files.map((f) => f.key))
							);
							await axios.post(
								`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/copy`,
								{
									bucket: resourceId,
									destinationBucket: resourceId,
									sourceKey: file.key,
									destinationKey: targetKey
								},
								{
									headers: {
										Authorization: `Bearer ${token}`
									}
								}
							);
						} else {
							const sourceFolderPrefix = file.key.endsWith('/')
								? file.key
								: file.key + '/';
							const folderBasename =
								sourceFolderPrefix
									.slice(0, -1)
									.split('/')
									.pop() || 'folder';
							const parentPath = sourceFolderPrefix.substring(
								0,
								sourceFolderPrefix.length -
									folderBasename.length -
									1
							);
							const existingFolderKeys = new Set(
								files.map((f) =>
									f.key.endsWith('/')
										? f.key.slice(0, -1)
										: f.key
								)
							);
							const targetFolderName = generateUniqueKey(
								parentPath + folderBasename,
								existingFolderKeys
							)
								.split('/')
								.pop()!;

							const destinationFolderPrefix =
								parentPath + targetFolderName + '/';

							await axios.post(
								`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/copy`,
								{
									bucket: resourceId,
									destinationBucket: resourceId,
									sourceKey: sourceFolderPrefix,
									destinationKey: destinationFolderPrefix
								},
								{
									headers: {
										Authorization: `Bearer ${token}`
									}
								}
							);
						}
					})
				);

				completedCount += chunk.length;
				setCopyProgress((prev) => ({
					...prev,
					completed: completedCount,
					title: `Cloning ${completedCount} of ${selectedItems.length} items...`
				}));
			}

			popupNotification({
				text: `Successfully cloned ${selectedItems.length} items`,
				color: 'success'
			});
			setSelectedKeys(new Set());
			fetchFiles();
		} catch (err: any) {
			console.error('Error batch cloning items:', err);
			popupNotification({
				text: 'Failed to clone items',
				color: 'error'
			});
		} finally {
			setIsCopyingBatch(false);
			setCopyProgress({ total: 0, completed: 0, title: '' });
		}
	};

	const handleOpenRenameDialog = (file: IFile) => {
		const isFolder = (file as any).isFolder || file.key.endsWith('/');
		const cleanKey =
			isFolder && file.key.endsWith('/')
				? file.key.slice(0, -1)
				: file.key;
		const baseName = cleanKey.split('/').pop() || '';
		setRenameTargetFile(file);
		setRenameNewName(baseName);
		setIsRenameDialogOpen(true);
	};

	const handleRenameSubmit = async () => {
		if (!renameTargetFile || !renameNewName) return;
		const isFolder =
			(renameTargetFile as any).isFolder ||
			renameTargetFile.key.endsWith('/');
		const cleanKey =
			isFolder && renameTargetFile.key.endsWith('/')
				? renameTargetFile.key.slice(0, -1)
				: renameTargetFile.key;
		const lastSlash = cleanKey.lastIndexOf('/');
		const parentPath =
			lastSlash !== -1 ? cleanKey.substring(0, lastSlash + 1) : '';

		const destinationKey =
			parentPath + renameNewName + (isFolder ? '/' : '');

		if (destinationKey === renameTargetFile.key) {
			setIsRenameDialogOpen(false);
			return;
		}

		setIsRenaming(true);
		setOperatingKeys((prev) => new Set(prev).add(renameTargetFile.key));

		try {
			const token = await getAccessToken();
			await axios.post(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/rename`,
				{
					bucket: resourceId,
					key: renameTargetFile.key,
					newName: renameNewName
				},
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			);
			popupNotification({
				text: `Renamed to "${renameNewName}"`,
				color: 'success'
			});
			setIsRenameDialogOpen(false);
			setRenameTargetFile(null);
			setRenameNewName('');
			fetchFiles();
		} catch (err: any) {
			console.error('Error renaming item:', err);
			popupNotification({
				text: 'Failed to rename item',
				color: 'error'
			});
		} finally {
			setIsRenaming(false);
			if (renameTargetFile) {
				setOperatingKeys((prev) => {
					const next = new Set(prev);
					next.delete(renameTargetFile.key);
					return next;
				});
			}
		}
	};

	const handleToggleFileAccess = async (file: IFile) => {
		const bucketAccessRaw =
			(bucketEntity?.details as any)?.access ||
			((bucketEntity?.details as any)?.publicReadAccess
				? 'public-read'
				: 'private');

		const isBucketPublic =
			bucketAccessRaw.toLowerCase() === 'public' ||
			bucketAccessRaw.toLowerCase() === 'public-read';

		const fileAccessRaw = (file as any).access;
		const isFilePublic = fileAccessRaw
			? fileAccessRaw.toLowerCase() === 'public' ||
				fileAccessRaw.toLowerCase() === 'public-read'
			: isBucketPublic;

		const targetAccess = isFilePublic ? 'private' : 'public-read';

		setOperatingKeys((prev) => new Set(prev).add(file.key));

		try {
			const token = await getAccessToken();
			await axios.post(
				`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/access`,
				{
					bucket: resourceId,
					key: file.key,
					access: targetAccess
				},
				{
					headers: { Authorization: `Bearer ${token}` }
				}
			);

			const itemBasename =
				file.key.split('/').filter(Boolean).pop() || 'item';
			popupNotification({
				text: `Set access for "${itemBasename}" to ${targetAccess === 'private' ? 'Private' : 'Public'}`,
				color: 'success'
			});
			fetchFiles();
		} catch (err: any) {
			console.error('Error updating access:', err);
			popupNotification({
				text: 'Failed to update access permissions',
				color: 'error'
			});
		} finally {
			setOperatingKeys((prev) => {
				const next = new Set(prev);
				next.delete(file.key);
				return next;
			});
		}
	};

	const handleBulkMakePublic = async () => {
		if (itemsToMakePublic.length === 0) return;
		setIsUpdatingBulkAccess(true);
		try {
			const token = await getAccessToken();
			const CHUNK_SIZE = 5;
			for (let i = 0; i < itemsToMakePublic.length; i += CHUNK_SIZE) {
				const chunk = itemsToMakePublic.slice(i, i + CHUNK_SIZE);
				await Promise.all(
					chunk.map((item) =>
						axios.post(
							`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/access`,
							{
								bucket: resourceId,
								key: item.key,
								access: 'public-read'
							},
							{
								headers: { Authorization: `Bearer ${token}` }
							}
						)
					)
				);
			}
			popupNotification({
				text: `Set access to Public for ${itemsToMakePublic.length} ${itemsToMakePublic.length === 1 ? 'item' : 'items'}`,
				color: 'success'
			});
			fetchFiles();
		} catch (err: any) {
			console.error('Error setting bulk public access:', err);
			popupNotification({
				text: 'Failed to update access permissions for selected items',
				color: 'error'
			});
		} finally {
			setIsUpdatingBulkAccess(false);
		}
	};

	const handleBulkMakePrivate = async () => {
		if (itemsToMakePrivate.length === 0) return;
		setIsUpdatingBulkAccess(true);
		try {
			const token = await getAccessToken();
			const CHUNK_SIZE = 5;
			for (let i = 0; i < itemsToMakePrivate.length; i += CHUNK_SIZE) {
				const chunk = itemsToMakePrivate.slice(i, i + CHUNK_SIZE);
				await Promise.all(
					chunk.map((item) =>
						axios.post(
							`${envConfig.API_BASE_URL}/cloud-files-service/v1/files/access`,
							{
								bucket: resourceId,
								key: item.key,
								access: 'private'
							},
							{
								headers: { Authorization: `Bearer ${token}` }
							}
						)
					)
				);
			}
			popupNotification({
				text: `Set access to Private for ${itemsToMakePrivate.length} ${itemsToMakePrivate.length === 1 ? 'item' : 'items'}`,
				color: 'success'
			});
			fetchFiles();
		} catch (err: any) {
			console.error('Error setting bulk private access:', err);
			popupNotification({
				text: 'Failed to update access permissions for selected items',
				color: 'error'
			});
		} finally {
			setIsUpdatingBulkAccess(false);
		}
	};

	const clearHoverTimer = () => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
	};

	const handleFolderDragOver = (
		e: React.DragEvent,
		targetFolderPath: string
	) => {
		e.preventDefault();
		e.stopPropagation();

		// Do not highlight or navigate to the current active path (last breadcrumb path)
		if (targetFolderPath === path) {
			return;
		}

		// Prevent dragging/hovering a folder into itself or any of its subfolders
		const activeItems =
			draggedItems.length > 0
				? draggedItems
				: draggedItem
					? [draggedItem]
					: [];
		for (const item of activeItems) {
			const isDraggedFolder =
				(item as any).isFolder || item.key.endsWith('/');
			const folderPrefix = item.key.endsWith('/')
				? item.key
				: item.key + '/';
			if (
				isDraggedFolder &&
				(targetFolderPath === item.key ||
					targetFolderPath.startsWith(folderPrefix))
			) {
				return;
			}
		}

		if (dragHoverTargetKey !== targetFolderPath) {
			setDragHoverTargetKey(targetFolderPath);
			clearHoverTimer();
			hoverTimerRef.current = setTimeout(() => {
				setUrlParam('path', targetFolderPath);
				popupNotification({
					text: `Navigated to /${targetFolderPath}`,
					color: 'primary'
				});
			}, 600);
		}
	};

	const handleFolderDragLeave = (e: React.DragEvent) => {
		e.stopPropagation();
		clearHoverTimer();
		setDragHoverTargetKey(null);
	};

	const executeMove = async (
		moveItems: IFile[],
		targetFolderPath: string
	) => {
		setOperatingKeys((prev) => {
			const next = new Set(prev);
			moveItems.forEach((item) => next.add(item.key));
			return next;
		});

		try {
			setIsMoving(true);
			maxMoveBatchTotalRef.current = moveItems.length;
			setMoveProgress({ total: moveItems.length, completed: 0 });

			await MoveManagerService.moveItemsBatch({
				bucket: resourceId,
				items: moveItems.map((item) => ({
					key: item.key,
					isFolder: (item as any).isFolder || item.key.endsWith('/')
				})),
				targetFolderPath,
				getAccessToken,
				envConfig,
				onComplete: () => {
					popupNotification({
						text: `Moved ${moveItems.length} ${moveItems.length === 1 ? 'item' : 'items'} to /${targetFolderPath}`,
						color: 'success'
					});
					setSelectedKeys(new Set());
					fetchFiles();
				},
				onError: (err) => {
					console.error('Error moving item(s):', err);
					popupNotification({
						text: 'Failed to move items',
						color: 'error'
					});
				}
			});
		} catch (err: any) {
			console.error('Error initiating move batch:', err);
			popupNotification({ text: 'Failed to move items', color: 'error' });
		} finally {
			setOperatingKeys((prev) => {
				const next = new Set(prev);
				moveItems.forEach((item) => next.delete(item.key));
				return next;
			});
			setDraggedItem(null);
			setDraggedItems([]);
		}
	};

	const handleDropOnTargetFolder = async (
		e: React.DragEvent,
		targetFolderPath: string
	) => {
		e.preventDefault();
		e.stopPropagation();
		clearHoverTimer();
		setDragHoverTargetKey(null);

		const itemsToMove =
			draggedItems.length > 0
				? draggedItems
				: draggedItem
					? [draggedItem]
					: [];
		if (itemsToMove.length === 0) return;

		// Filter out invalid targets (e.g. already in destination, or folder moving into itself)
		const validMoveItems = itemsToMove.filter((item) => {
			const isFolder = (item as any).isFolder || item.key.endsWith('/');
			const cleanKey =
				isFolder && item.key.endsWith('/')
					? item.key.slice(0, -1)
					: item.key;
			const itemBasename = cleanKey.split('/').pop() || '';
			const destinationKey =
				targetFolderPath + itemBasename + (isFolder ? '/' : '');

			if (destinationKey === item.key) return false;
			if (isFolder && targetFolderPath.startsWith(item.key)) return false;
			return true;
		});

		if (validMoveItems.length === 0) {
			setDraggedItem(null);
			setDraggedItems([]);
			return;
		}

		const hasConflict = validMoveItems.some((item) => {
			const isFolder = (item as any).isFolder || item.key.endsWith('/');
			const cleanKey =
				isFolder && item.key.endsWith('/')
					? item.key.slice(0, -1)
					: item.key;
			const itemBasename = cleanKey.split('/').pop() || '';
			const destinationKey =
				targetFolderPath + itemBasename + (isFolder ? '/' : '');
			return files.some(
				(existingItem) => existingItem.key === destinationKey
			);
		});

		if (hasConflict) {
			setPendingMove({
				sourceItems: validMoveItems,
				destinationFolderPath: targetFolderPath
			});
			setIsMoveConflictModalOpen(true);
			return;
		}

		await executeMove(validMoveItems, targetFolderPath);
	};

	const handleConfirmMove = (overwrite: boolean) => {
		if (!pendingMove) return;
		const { sourceItems, destinationFolderPath } = pendingMove;
		setIsMoveConflictModalOpen(false);
		setPendingMove(null);
		executeMove(sourceItems, destinationFolderPath);
	};

	const handleCancelMove = () => {
		setIsMoveConflictModalOpen(false);
		setPendingMove(null);
		setDraggedItem(null);
		setDraggedItems([]);
	};

	const setUrlParam = (key: string, value: string) => {
		setSearchParams((prev) => {
			const newParams = new URLSearchParams(prev);
			if (value) {
				newParams.set(key, value);
			} else {
				newParams.delete(key);
			}
			return newParams;
		});
	};

	// --- Derived View Data ---
	const filteredFiles = useMemo(() => {
		let f = files;
		if (searchQuery) {
			f = f.filter((file) =>
				file.key.toLowerCase().includes(searchQuery.toLowerCase())
			);
		}
		if (typeFilter.length > 0) {
			f = f.filter((file) => {
				return typeFilter.some((ext) =>
					file.key.toLowerCase().endsWith(ext)
				);
			});
		}
		if (dateFrom) {
			const fromTime = new Date(dateFrom).getTime();
			f = f.filter(
				(file) => new Date(file.lastModified).getTime() >= fromTime
			);
		}
		if (dateTo) {
			const toTime = new Date(dateTo).getTime();
			f = f.filter(
				(file) => new Date(file.lastModified).getTime() <= toTime
			);
		}
		if (accessFilter) {
			const targetAccessIsPublic =
				accessFilter.toLowerCase() === 'public';
			const bucketAccessRaw =
				(bucketEntity?.details as any)?.access ||
				((bucketEntity?.details as any)?.publicReadAccess
					? 'public-read'
					: 'private');
			const isBucketPublic =
				bucketAccessRaw.toLowerCase() === 'public' ||
				bucketAccessRaw.toLowerCase() === 'public-read';

			f = f.filter((file) => {
				const fileAccessRaw = (file as any).access;
				const isFilePublic = fileAccessRaw
					? fileAccessRaw.toLowerCase() === 'public' ||
						fileAccessRaw.toLowerCase() === 'public-read'
					: isBucketPublic;
				return targetAccessIsPublic ? isFilePublic : !isFilePublic;
			});
		}
		return f;
	}, [
		files,
		searchQuery,
		typeFilter,
		dateFrom,
		dateTo,
		accessFilter,
		bucketEntity
	]);

	const currentViewItems = useMemo(() => {
		if (viewMode === 'list') {
			return filteredFiles.map((file) => {
				const isFolder = file.key.endsWith('/');
				const name = isFolder
					? file.key.slice(0, -1).split('/').pop() || ''
					: file.key.split('/').pop() || '';
				return {
					...file,
					isFolder,
					name
				};
			});
		}

		// Tree mode
		const items: (IFile & { isFolder: boolean; name: string })[] = [];

		// Compute total aggregate file size, latest lastModified date, and access set per folder
		const folderMap = new Map<
			string,
			{ size: number; latestLastModified: number; accesses: Set<string> }
		>();

		const bucketAccessRaw =
			(bucketEntity?.details as any)?.access ||
			((bucketEntity?.details as any)?.publicReadAccess
				? 'public-read'
				: 'private');

		const isBucketPublic =
			bucketAccessRaw.toLowerCase() === 'public' ||
			bucketAccessRaw.toLowerCase() === 'public-read';

		filteredFiles.forEach((file) => {
			if (!file.key.startsWith(path)) return;
			const relativeKey = file.key.substring(path.length);
			if (relativeKey === '') return;

			const slashIndex = relativeKey.indexOf('/');
			if (slashIndex !== -1) {
				const folderName = relativeKey.substring(0, slashIndex);
				const fileTime = new Date(file.lastModified).getTime() || 0;

				const fileAccessRaw = (file as any).access;
				const isFilePublic = fileAccessRaw
					? fileAccessRaw.toLowerCase() === 'public' ||
						fileAccessRaw.toLowerCase() === 'public-read'
					: isBucketPublic;
				const normalizedFileAccess = isFilePublic
					? 'public-read'
					: 'private';

				const current = folderMap.get(folderName) || {
					size: 0,
					latestLastModified: 0,
					accesses: new Set<string>()
				};
				current.accesses.add(normalizedFileAccess);
				folderMap.set(folderName, {
					size: current.size + (file.size || 0),
					latestLastModified: Math.max(
						current.latestLastModified,
						fileTime
					),
					accesses: current.accesses
				});
			}
		});

		const getFolderAccess = (folderName: string) => {
			const folderKey = path + folderName + '/';
			const explicitObj = files.find((f) => f.key === folderKey);
			if ((explicitObj as any)?.access) {
				return (explicitObj as any).access;
			}
			const folderInfo = folderMap.get(folderName);
			if (folderInfo && folderInfo.accesses.size > 0) {
				if (
					folderInfo.accesses.has('public-read') &&
					!folderInfo.accesses.has('private')
				) {
					return 'public-read';
				}
				if (
					folderInfo.accesses.has('private') &&
					!folderInfo.accesses.has('public-read')
				) {
					return 'private';
				}
			}
			return undefined;
		};

		const folderSet = new Set<string>();

		filteredFiles.forEach((file) => {
			if (!file.key.startsWith(path)) return;
			const relativeKey = file.key.substring(path.length);
			if (relativeKey === '') return;

			const slashIndex = relativeKey.indexOf('/');
			if (slashIndex === -1) {
				items.push({ ...file, isFolder: false, name: relativeKey });
			} else {
				const folderName = relativeKey.substring(0, slashIndex);
				if (!folderSet.has(folderName)) {
					folderSet.add(folderName);
					const folderInfo = folderMap.get(folderName) || {
						size: 0,
						latestLastModified: 0,
						accesses: new Set<string>()
					};
					items.push({
						key: path + folderName + '/',
						size: folderInfo.size,
						lastModified: folderInfo.latestLastModified
							? new Date(
									folderInfo.latestLastModified
								).toISOString()
							: file.lastModified,
						isFolder: true,
						name: folderName,
						access: getFolderAccess(folderName)
					} as any);
				}
			}
		});
		return items.sort((a, b) => {
			if (a.isFolder && !b.isFolder) return -1;
			if (!a.isFolder && b.isFolder) return 1;
			return a.name.localeCompare(b.name);
		});
	}, [filteredFiles, files, path, viewMode, bucketEntity]);

	const itemsToMakePublic = useMemo(() => {
		if (selectedKeys.size === 0) return [];
		const bucketAccessRaw =
			(bucketEntity?.details as any)?.access ||
			((bucketEntity?.details as any)?.publicReadAccess
				? 'public-read'
				: 'private');

		const isBucketPublic =
			bucketAccessRaw.toLowerCase() === 'public' ||
			bucketAccessRaw.toLowerCase() === 'public-read';

		const selectedItems = currentViewItems.filter((f) =>
			selectedKeys.has(f.key)
		);

		return selectedItems.filter((item) => {
			const raw = (item as any).access;
			const isItemPublic = raw
				? raw.toLowerCase() === 'public' ||
					raw.toLowerCase() === 'public-read'
				: isBucketPublic;
			return !isItemPublic;
		});
	}, [selectedKeys, currentViewItems, bucketEntity]);

	const itemsToMakePrivate = useMemo(() => {
		if (selectedKeys.size === 0) return [];
		const bucketAccessRaw =
			(bucketEntity?.details as any)?.access ||
			((bucketEntity?.details as any)?.publicReadAccess
				? 'public-read'
				: 'private');

		const isBucketPublic =
			bucketAccessRaw.toLowerCase() === 'public' ||
			bucketAccessRaw.toLowerCase() === 'public-read';

		const selectedItems = currentViewItems.filter((f) =>
			selectedKeys.has(f.key)
		);

		return selectedItems.filter((item) => {
			const raw = (item as any).access;
			const isItemPublic = raw
				? raw.toLowerCase() === 'public' ||
					raw.toLowerCase() === 'public-read'
				: isBucketPublic;
			return isItemPublic;
		});
	}, [selectedKeys, currentViewItems, bucketEntity]);

	const breadcrumbs = path.split('/').filter(Boolean);

	return (
		<div
			style={{
				padding: '16px 40px 40px 40px',
				height: '100%',
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			{/* Top Header / Banner */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '20px'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '12px'
					}}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '4px'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px',
								marginTop: '4px'
							}}
						>
							<img
								src={CloudFileSystemIcon}
								alt=""
								style={{
									width: '22px',
									height: '22px',
									objectFit: 'contain'
								}}
							/>
							<h2
								style={{
									margin: '0px',
									fontSize: '18px',
									fontWeight: 600,
									color: '#111827'
								}}
							>
								{bucketEntity?.name || resourceId}
							</h2>
						</div>
						<span
							style={{
								fontSize: '12px',
								color: '#6b7280',
								marginTop: '2px'
							}}
						>
							Cloud File System
						</span>
					</div>
				</div>
				<div style={{ display: 'flex', gap: '10px' }}>
					<Button
						variant="outlined"
						size="small"
						iconStart="fa-solid fa-rotate"
						loading={isLoading}
						onClick={() => fetchFiles()}
					>
						Refresh
					</Button>
				</div>
			</div>
			{/* Bucket Config Display */}
			<div
				style={{
					display: 'flex',
					gap: '20px',
					marginBottom: '20px',
					padding: '12px 16px',
					backgroundColor: '#f9fafb',
					borderRadius: '8px',
					border: '1px solid #e5e7eb',
					flexWrap: 'wrap'
				}}
			>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px'
					}}
				>
					<span
						style={{
							fontSize: '11px',
							color: '#6b7280',
							textTransform: 'uppercase',
							letterSpacing: '0.5px'
						}}
					>
						Total Files
					</span>
					<span
						style={{
							fontSize: '13px',
							fontWeight: 500,
							color: '#374151'
						}}
					>
						{files.length}
					</span>
				</div>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px'
					}}
				>
					<span
						style={{
							fontSize: '11px',
							color: '#6b7280',
							textTransform: 'uppercase',
							letterSpacing: '0.5px'
						}}
					>
						Total Storage
					</span>
					<span
						style={{
							fontSize: '13px',
							fontWeight: 500,
							color: '#374151'
						}}
					>
						{formatBytes(files.reduce((acc, f) => acc + f.size, 0))}
					</span>
				</div>
				{bucketEntity?.details && (
					<>
						<div
							style={{
								width: '1px',
								backgroundColor: '#e5e7eb',
								margin: '0 8px'
							}}
						></div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								Access
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: (bucketEntity.details as any)
										?.publicReadAccess
										? '#ef4444'
										: '#10b981'
								}}
							>
								{(bucketEntity.details as any)?.publicReadAccess
									? 'Public (Read)'
									: 'Private'}
							</span>
							{oppositeAccessFilesCount > 0 && (
								<span
									style={{
										fontSize: '11px',
										fontWeight: 500,
										color: (bucketEntity.details as any)
											?.publicReadAccess
											? '#b45309'
											: '#15803d',
										marginTop: '2px'
									}}
								>
									({oppositeAccessFilesCount}{' '}
									{(bucketEntity.details as any)
										?.publicReadAccess
										? 'private'
										: 'public'}{' '}
									{oppositeAccessFilesCount === 1
										? 'file'
										: 'files'}
									)
								</span>
							)}
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								Website Hosting
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: (bucketEntity.details as any)
										?.websiteHostingEnabled
										? '#10b981'
										: '#6b7280',
									display: 'flex',
									alignItems: 'center',
									gap: '6px'
								}}
							>
								{(bucketEntity.details as any)
									?.websiteHostingEnabled
									? 'Enabled'
									: 'Disabled'}
								{(bucketEntity.details as any)
									?.websiteHostingEnabled &&
									(() => {
										const websiteUrl = `http://${resourceId}.s3-website.${envConfig.amplifyConfig.aws_project_region}.amazonaws.com`;
										return (
											<>
												<Tooltip
													title={
														<span
															style={{
																display: 'flex',
																flexDirection:
																	'column',
																alignItems:
																	'center'
															}}
														>
															<b>
																Open website on
																a new tab
															</b>
															<br />
															{websiteUrl}
														</span>
													}
													arrow
													placement="top"
												>
													<a
														href={websiteUrl}
														target="_blank"
														rel="noreferrer"
														style={{
															color: '#4f46e5',
															textDecoration:
																'none',
															display: 'flex',
															alignItems:
																'center',
															backgroundColor:
																'#e0e7ff',
															padding: '2px 5px',
															borderRadius: '4px'
														}}
													>
														<i
															className="fa-solid fa-external-link-alt"
															style={{
																fontSize: '11px'
															}}
														></i>
													</a>
												</Tooltip>
												<Tooltip
													title={
														<span
															style={{
																display: 'flex',
																flexDirection:
																	'column',
																alignItems:
																	'center'
															}}
														>
															<b>
																Click to copy
																the URL to your
																clipboard
															</b>
															<br />
															{websiteUrl}
														</span>
													}
													arrow
													placement="top"
												>
													<button
														onClick={() => {
															navigator.clipboard.writeText(
																websiteUrl
															);
															popupNotification({
																text: 'Copied website URL!',
																color: 'primary'
															});
														}}
														style={{
															backgroundColor:
																'#e0e7ff',
															color: '#4f46e5',
															border: 'none',
															borderRadius: '4px',
															padding: '2px 6px',
															fontSize: '10px',
															fontWeight: 700,
															cursor: 'pointer',
															display: 'flex',
															alignItems:
																'center',
															lineHeight: '1.2'
														}}
													>
														URL
													</button>
												</Tooltip>
											</>
										);
									})()}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								HTTPS
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: (bucketEntity.details as any)
										?.requireHttps
										? '#10b981'
										: '#f59e0b'
								}}
							>
								{(bucketEntity.details as any)?.requireHttps
									? 'Required'
									: 'Optional'}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								gap: '4px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#6b7280',
									textTransform: 'uppercase',
									letterSpacing: '0.5px'
								}}
							>
								CORS
							</span>
							<span
								style={{
									fontSize: '13px',
									fontWeight: 500,
									color: '#374151',
									maxWidth: '150px',
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis'
								}}
								title={(
									bucketEntity.details as any
								)?.allowedOrigins?.join(', ')}
							>
								{(bucketEntity.details as any)?.allowedOrigins
									?.length
									? (
											bucketEntity.details as any
										).allowedOrigins.join(', ')
									: 'None'}
							</span>
						</div>

						{/* Index Document */}
						{(bucketEntity.details as any)?.websiteHostingEnabled &&
							(bucketEntity.details as any)?.indexDocument && (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '4px'
									}}
								>
									<span
										style={{
											fontSize: '11px',
											color: '#6b7280',
											textTransform: 'uppercase',
											letterSpacing: '0.5px'
										}}
									>
										Index Doc
									</span>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px'
										}}
									>
										{files.some(
											(f) =>
												f.key ===
												(bucketEntity.details as any)
													.indexDocument
										) ? (
											<a
												style={{
													fontSize: '13px',
													fontWeight: 500,
													color: '#4f46e5',
													textDecoration: 'none',
													cursor: 'pointer'
												}}
												onClick={(e) => {
													e.preventDefault();
													navigate(
														`view/${encodeURIComponent((bucketEntity.details as any).indexDocument)}`
													);
												}}
											>
												{
													(
														bucketEntity.details as any
													).indexDocument
												}
											</a>
										) : (
											<>
												<span
													style={{
														fontSize: '13px',
														fontWeight: 500,
														color: 'var(--color-main-warning)'
													}}
												>
													{
														(
															bucketEntity.details as any
														).indexDocument
													}
												</span>
												<Tooltip
													title="File not found in storage"
													arrow
													placement="top"
												>
													<img
														src={warningIcon}
														alt="Warning"
														style={{
															width: '16px',
															height: '16px'
														}}
													/>
												</Tooltip>
											</>
										)}
									</div>
								</div>
							)}

						{/* Error Document */}
						{(bucketEntity.details as any)?.websiteHostingEnabled &&
							(bucketEntity.details as any)?.errorDocument && (
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '4px'
									}}
								>
									<span
										style={{
											fontSize: '11px',
											color: '#6b7280',
											textTransform: 'uppercase',
											letterSpacing: '0.5px'
										}}
									>
										Error Doc
									</span>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px'
										}}
									>
										{files.some(
											(f) =>
												f.key ===
												(bucketEntity.details as any)
													.errorDocument
										) ? (
											<a
												style={{
													fontSize: '13px',
													fontWeight: 500,
													color: '#4f46e5',
													textDecoration: 'none',
													cursor: 'pointer'
												}}
												onClick={(e) => {
													e.preventDefault();
													navigate(
														`view/${encodeURIComponent((bucketEntity.details as any).errorDocument)}`
													);
												}}
											>
												{
													(
														bucketEntity.details as any
													).errorDocument
												}
											</a>
										) : (
											<>
												<span
													style={{
														fontSize: '13px',
														fontWeight: 500,
														color: 'var(--color-main-warning)'
													}}
												>
													{
														(
															bucketEntity.details as any
														).errorDocument
													}
												</span>
												<Tooltip
													title="File not found in storage"
													arrow
													placement="top"
												>
													<img
														src={warningIcon}
														alt="Warning"
														style={{
															width: '16px',
															height: '16px'
														}}
													/>
												</Tooltip>
											</>
										)}
									</div>
								</div>
							)}
					</>
				)}
			</div>
			{/* Toolbar (Search, Filter, View Toggles, Actions) */}
			<div
				className="file-explorer--toolbar"
				style={
					!!selectedKeys.size
						? {
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								alignItems: 'flex-start',
								gap: '12px'
							}
						: {}
				}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '12px'
					}}
				>
					{/* Search & Filter */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							backgroundColor: '#fff',
							border: '1px solid #e2e8f0',
							borderRadius: '6px',
							padding: '4px 8px'
						}}
					>
						<i
							className="fa-solid fa-search"
							style={{ color: '#94a3b8', marginRight: '8px' }}
						></i>
						<input
							type="text"
							placeholder="Search files..."
							value={searchQuery}
							onChange={(e) =>
								setUrlParam('search', e.target.value)
							}
							style={{
								border: 'none',
								outline: 'none',
								fontSize: '14px',
								width: '150px'
							}}
						/>
					</div>
					<div
						style={{
							position: 'relative',
							display: 'flex',
							alignItems: 'center',
							gap: '8px'
						}}
					>
						<button
							onClick={(e) => setFilterAnchorEl(e.currentTarget)}
							style={{
								border: '1px solid #e2e8f0',
								borderRadius: '6px',
								padding: '6px 12px',
								background: filterAnchorEl ? '#f1f5f9' : '#fff',
								cursor: 'pointer',
								fontSize: '14px',
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								color: '#334155'
							}}
						>
							<i
								className="fa-solid fa-filter"
								style={{ color: '#64748b' }}
							></i>
							Filters
							{(typeFilter.length > 0 ||
								dateFrom ||
								dateTo ||
								accessFilter) && (
								<div
									style={{
										background: '#4f46e5',
										color: '#fff',
										borderRadius: '10px',
										padding: '0 6px',
										height: '20px',
										minWidth: '20px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '12px',
										fontWeight: 'bold',
										marginLeft: '4px'
									}}
								>
									{typeFilter.length +
										(dateFrom ? 1 : 0) +
										(dateTo ? 1 : 0) +
										(accessFilter ? 1 : 0)}
								</div>
							)}
						</button>
						{(typeFilter.length > 0 ||
							dateFrom ||
							dateTo ||
							accessFilter) && (
							<Tooltip title="Clear all filters" arrow>
								<IconButton
									size="small"
									onClick={() => {
										const newParams = new URLSearchParams(
											searchParams
										);
										newParams.delete('type');
										newParams.delete('dateFrom');
										newParams.delete('dateTo');
										newParams.delete('access');
										setSearchParams(newParams);
									}}
								>
									<i
										className="fa-solid fa-xmark"
										style={{ fontSize: '14px' }}
									></i>
								</IconButton>
							</Tooltip>
						)}
					</div>

					{/* View toggles */}
					<div style={{ display: 'flex', gap: '12px' }}>
						<div
							style={{
								display: 'flex',
								border: '1px solid #e2e8f0',
								borderRadius: '6px',
								overflow: 'hidden'
							}}
						>
							<Tooltip title="Tree View" arrow>
								<button
									onClick={() => setUrlParam('view', 'tree')}
									style={{
										background:
											viewMode === 'tree'
												? '#e0e7ff'
												: '#fff',
										border: 'none',
										padding: '6px 10px',
										cursor: 'pointer',
										color:
											viewMode === 'tree'
												? '#4f46e5'
												: '#64748b'
									}}
								>
									<i className="fa-solid fa-folder-tree"></i>
								</button>
							</Tooltip>
							<Tooltip title="Flat List View" arrow>
								<button
									onClick={() => setUrlParam('view', 'list')}
									style={{
										background:
											viewMode === 'list'
												? '#e0e7ff'
												: '#fff',
										border: 'none',
										padding: '6px 10px',
										cursor: 'pointer',
										borderLeft: '1px solid #e2e8f0',
										color:
											viewMode === 'list'
												? '#4f46e5'
												: '#64748b'
									}}
								>
									<i className="fa-solid fa-list"></i>
								</button>
							</Tooltip>
						</div>

						{/* Layout toggles */}
						<div
							style={{
								display: 'flex',
								border: '1px solid #e2e8f0',
								borderRadius: '6px',
								overflow: 'hidden'
							}}
						>
							<Tooltip title="Table Layout" arrow>
								<button
									onClick={() =>
										setUrlParam('layout', 'table')
									}
									style={{
										background:
											layoutMode === 'table'
												? '#e0e7ff'
												: '#fff',
										border: 'none',
										padding: '6px 10px',
										cursor: 'pointer',
										color:
											layoutMode === 'table'
												? '#4f46e5'
												: '#64748b'
									}}
								>
									<i className="fa-solid fa-table"></i>
								</button>
							</Tooltip>
							<Tooltip title="Grid Layout" arrow>
								<button
									onClick={() =>
										setUrlParam('layout', 'grid')
									}
									style={{
										background:
											layoutMode === 'grid'
												? '#e0e7ff'
												: '#fff',
										border: 'none',
										padding: '6px 10px',
										cursor: 'pointer',
										borderLeft: '1px solid #e2e8f0',
										color:
											layoutMode === 'grid'
												? '#4f46e5'
												: '#64748b'
									}}
								>
									<i className="fa-solid fa-border-all"></i>
								</button>
							</Tooltip>
						</div>
					</div>
				</div>

				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						flexWrap: 'wrap',
						justifyContent: 'flex-start'
					}}
				>
					{selectedKeys.size > 0 && (
						<>
							<Button
								size="small"
								variant="outlined"
								iconStart="fa-solid fa-clone"
								onClick={handleBulkClone}
							>
								Clone ({expandedSelectedFiles.length})
							</Button>
							<Button
								size="small"
								variant="outlined"
								iconStart="fa-solid fa-download"
								loading={isDownloading}
								disabled={isDownloading}
								onClick={handleBulkDownload}
							>
								Download ({expandedSelectedFiles.length})
							</Button>
							{itemsToMakePublic.length > 0 && (
								<Button
									size="small"
									variant="outlined"
									iconStart="fa-solid fa-globe"
									loading={isUpdatingBulkAccess}
									disabled={isUpdatingBulkAccess}
									onClick={handleBulkMakePublic}
								>
									Make Public ({itemsToMakePublic.length})
								</Button>
							)}
							{itemsToMakePrivate.length > 0 && (
								<Button
									size="small"
									variant="outlined"
									iconStart="fa-solid fa-lock"
									loading={isUpdatingBulkAccess}
									disabled={isUpdatingBulkAccess}
									onClick={handleBulkMakePrivate}
								>
									Make Private ({itemsToMakePrivate.length})
								</Button>
							)}
							<Button
								size="small"
								variant="outlined"
								iconStart="fa-solid fa-trash"
								onClick={() => setIsBulkDeleteDialogOpen(true)}
								style={{
									color: '#ef4444',
									borderColor: '#ef4444'
								}}
							>
								Delete ({expandedSelectedFiles.length})
							</Button>
						</>
					)}
					{selectedKeys.size === 0 && viewMode === 'tree' && (
						<Button
							size="small"
							variant="outlined"
							iconStart="fa-solid fa-folder-plus"
							onClick={() => setIsFolderDialogOpen(true)}
						>
							New Folder
						</Button>
					)}
					{selectedKeys.size === 0 && (
						<>
							<input
								ref={fileInputRef}
								type="file"
								multiple
								style={{ display: 'none' }}
								onChange={handleFileInput}
							/>
							<input
								ref={folderInputRef}
								type="file"
								multiple
								{...({
									webkitdirectory: '',
									directory: ''
								} as any)}
								style={{ display: 'none' }}
								onChange={handleFileInput}
							/>
							<Button
								size="small"
								iconStart="fa-solid fa-upload"
								iconEnd="fa-solid fa-chevron-down"
								loading={isUploading}
								onClick={(e: React.MouseEvent<HTMLElement>) =>
									setUploadMenuAnchorEl(e.currentTarget)
								}
							>
								Upload
							</Button>
							<Menu
								anchorEl={uploadMenuAnchorEl}
								open={Boolean(uploadMenuAnchorEl)}
								onClose={() => setUploadMenuAnchorEl(null)}
								slotProps={{
									paper: {
										sx: {
											borderRadius: '8px',
											boxShadow:
												'0 4px 20px rgba(0, 0, 0, 0.15)'
										}
									}
								}}
							>
								<MenuItem
									onClick={() => {
										setUploadMenuAnchorEl(null);
										fileInputRef.current?.click();
									}}
									sx={{ gap: '10px', fontSize: '13px' }}
								>
									<i
										className="fa-solid fa-file"
										style={{ color: '#3b82f6' }}
									/>
									Upload Files
								</MenuItem>
								<MenuItem
									onClick={() => {
										setUploadMenuAnchorEl(null);
										folderInputRef.current?.click();
									}}
									sx={{ gap: '10px', fontSize: '13px' }}
								>
									<i
										className="fa-solid fa-folder"
										style={{ color: '#f59e0b' }}
									/>
									Upload Folder
								</MenuItem>
							</Menu>
						</>
					)}
				</div>
			</div>
			{/* Breadcrumbs for Tree View */}
			{viewMode === 'tree' && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						fontSize: '14px',
						color: '#64748b',
						marginBottom: '16px',
						flexWrap: 'wrap'
					}}
				>
					{(() => {
						const isRootCurrent = path === '';
						const isRootHovered =
							dragHoverTargetKey === '' && !isRootCurrent;
						return (
							<button
								onClick={() => setUrlParam('path', '')}
								onDragOver={
									isRootCurrent
										? undefined
										: (e) => handleFolderDragOver(e, '')
								}
								onDragLeave={
									isRootCurrent
										? undefined
										: handleFolderDragLeave
								}
								onDrop={
									isRootCurrent
										? undefined
										: (e) => handleDropOnTargetFolder(e, '')
								}
								style={{
									background: isRootHovered
										? '#e0e7ff'
										: 'none',
									border: isRootHovered
										? '2px dashed #4f46e5'
										: '1px solid transparent',
									borderRadius: '6px',
									padding: '4px 10px',
									cursor: isRootCurrent
										? 'default'
										: 'pointer',
									color: isRootHovered
										? '#4338ca'
										: isRootCurrent
											? '#1e293b'
											: '#6366f1',
									fontWeight:
										isRootHovered || isRootCurrent
											? 600
											: 400,
									transition: 'all 0.15s ease'
								}}
							>
								<i className="fa-solid fa-home"></i> Root
							</button>
						);
					})()}
					{breadcrumbs.map((crumb, idx) => {
						const currentCrumbPath =
							breadcrumbs.slice(0, idx + 1).join('/') + '/';
						const isLast = idx === breadcrumbs.length - 1;
						const isHovered =
							dragHoverTargetKey === currentCrumbPath && !isLast;
						return (
							<React.Fragment key={currentCrumbPath}>
								<i
									className="fa-solid fa-chevron-right"
									style={{ fontSize: '10px' }}
								></i>
								<button
									onClick={() =>
										setUrlParam('path', currentCrumbPath)
									}
									onDragOver={
										isLast
											? undefined
											: (e) =>
													handleFolderDragOver(
														e,
														currentCrumbPath
													)
									}
									onDragLeave={
										isLast
											? undefined
											: handleFolderDragLeave
									}
									onDrop={
										isLast
											? undefined
											: (e) =>
													handleDropOnTargetFolder(
														e,
														currentCrumbPath
													)
									}
									style={{
										background: isHovered
											? '#e0e7ff'
											: 'none',
										border: isHovered
											? '2px dashed #4f46e5'
											: '1px solid transparent',
										borderRadius: '6px',
										padding: '4px 10px',
										cursor: isLast ? 'default' : 'pointer',
										color: isHovered
											? '#4338ca'
											: isLast
												? '#1e293b'
												: '#6366f1',
										fontWeight:
											isHovered || isLast ? 600 : 400,
										transition: 'all 0.15s ease'
									}}
								>
									{crumb}
								</button>
							</React.Fragment>
						);
					})}
				</div>
			)}
			{/* Red Cancellation Banner */}
			{/* Batch Name Conflict Resolution Card */}
			{conflictState && (
				<div
					style={{
						backgroundColor: '#f8fafc',
						border: '1px solid #cbd5e1',
						borderRadius: '8px',
						padding: '14px 16px',
						marginBottom: '16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px'
					}}
				>
					{/* Header Row: Title & Global Action Buttons */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							borderBottom: '1px solid #e2e8f0',
							paddingBottom: '10px',
							flexWrap: 'wrap',
							gap: '10px'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<i
								className="fa-solid fa-triangle-exclamation"
								style={{ color: '#d97706', fontSize: '18px' }}
							/>
							<span
								style={{
									fontWeight: 600,
									color: '#1e293b',
									fontSize: '14px'
								}}
							>
								Name conflicts detected (
								{Object.keys(conflictState.resolutions).length}{' '}
								file
								{Object.keys(conflictState.resolutions).length >
								1
									? 's'
									: ''}
								)
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								fontSize: '12px',
								color: '#64748b'
							}}
						>
							<span style={{ fontWeight: 500 }}>
								Apply to all:
							</span>
							<Button
								size="small"
								variant="outlined"
								onClick={() => {
									setConflictState((prev) => {
										if (!prev) return null;
										const nextRes: Record<
											string,
											'replace' | 'keep_both' | 'skip'
										> = {};
										Object.keys(prev.resolutions).forEach(
											(k) => {
												nextRes[k] = 'replace';
											}
										);
										return {
											...prev,
											resolutions: nextRes
										};
									});
								}}
							>
								Replace All
							</Button>
							<Button
								size="small"
								variant="outlined"
								onClick={() => {
									setConflictState((prev) => {
										if (!prev) return null;
										const nextRes: Record<
											string,
											'replace' | 'keep_both' | 'skip'
										> = {};
										Object.keys(prev.resolutions).forEach(
											(k) => {
												nextRes[k] = 'keep_both';
											}
										);
										return {
											...prev,
											resolutions: nextRes
										};
									});
								}}
							>
								Keep Both All
							</Button>
							<Button
								size="small"
								variant="outlined"
								onClick={() => {
									setConflictState((prev) => {
										if (!prev) return null;
										const nextRes: Record<
											string,
											'replace' | 'keep_both' | 'skip'
										> = {};
										Object.keys(prev.resolutions).forEach(
											(k) => {
												nextRes[k] = 'skip';
											}
										);
										return {
											...prev,
											resolutions: nextRes
										};
									});
								}}
							>
								Skip All
							</Button>
						</div>
					</div>

					{/* Thin Conflict Item Rows */}
					<div
						style={{
							maxHeight: '220px',
							overflowY: 'auto',
							display: 'flex',
							flexDirection: 'column',
							gap: '6px'
						}}
					>
						{Object.entries(conflictState.resolutions).map(
							([key, resolution]) => (
								<div
									key={key}
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										padding: '8px 12px',
										backgroundColor: '#ffffff',
										borderRadius: '6px',
										border: '1px solid #e2e8f0',
										gap: '12px'
									}}
								>
									<span
										style={{
											fontSize: '13px',
											fontWeight: 500,
											color: '#334155',
											wordBreak: 'break-all'
										}}
									>
										{key}
									</span>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '16px',
											fontSize: '12px',
											flexShrink: 0
										}}
									>
										<label
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '5px',
												cursor: 'pointer',
												color: '#1e293b'
											}}
										>
											<input
												type="radio"
												name={`conflict_${key}`}
												checked={
													resolution === 'replace'
												}
												onChange={() => {
													setConflictState((prev) =>
														prev
															? {
																	...prev,
																	resolutions:
																		{
																			...prev.resolutions,
																			[key]: 'replace'
																		}
																}
															: null
													);
												}}
											/>
											Replace existing
										</label>
										<label
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '5px',
												cursor: 'pointer',
												color: '#1e293b'
											}}
										>
											<input
												type="radio"
												name={`conflict_${key}`}
												checked={
													resolution === 'keep_both'
												}
												onChange={() => {
													setConflictState((prev) =>
														prev
															? {
																	...prev,
																	resolutions:
																		{
																			...prev.resolutions,
																			[key]: 'keep_both'
																		}
																}
															: null
													);
												}}
											/>
											Keep both (rename)
										</label>
										<label
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '5px',
												cursor: 'pointer',
												color: '#1e293b'
											}}
										>
											<input
												type="radio"
												name={`conflict_${key}`}
												checked={resolution === 'skip'}
												onChange={() => {
													setConflictState((prev) =>
														prev
															? {
																	...prev,
																	resolutions:
																		{
																			...prev.resolutions,
																			[key]: 'skip'
																		}
																}
															: null
													);
												}}
											/>
											Skip upload
										</label>
									</div>
								</div>
							)
						)}
					</div>

					{/* Footer Action Buttons */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '10px',
							paddingTop: '6px',
							borderTop: '1px solid #e2e8f0'
						}}
					>
						<Button
							variant="outlined"
							size="small"
							onClick={() => setConflictState(null)}
						>
							Cancel Upload
						</Button>
						<Button
							size="small"
							onClick={handleProceedWithResolutions}
						>
							Continue Upload
						</Button>
					</div>
				</div>
			)}
			{cancellingInfo?.isCancelling && (
				<div
					style={{
						backgroundColor: '#fee2e2',
						border: '1px solid #fca5a5',
						borderRadius: '8px',
						padding: '12px 16px',
						marginBottom: '16px',
						display: 'flex',
						alignItems: 'center',
						gap: '12px'
					}}
				>
					<Loader size={20} color="error" variant="outlined" />
					<span
						style={{
							fontWeight: 500,
							color: '#991b1b',
							fontSize: '14px'
						}}
					>
						{cancellingInfo.deletingCount > 0
							? `Cancelling upload: deleting ${cancellingInfo.deletingCount} previously uploaded files...`
							: 'Cancelling upload...'}
					</span>
				</div>
			)}
			{/* Unified Upload Progress Card */}
			{!cancellingInfo && isUploading && (
				<div
					style={{
						backgroundColor: '#f8fafc',
						border: '1px solid #cbd5e1',
						borderRadius: '8px',
						padding: '12px 16px',
						marginBottom: '16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px'
					}}
				>
					{/* Header Row: Overall Batch Progress + IconButton '✕' */}
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px'
							}}
						>
							<Loader
								size={20}
								color="primary"
								variant="outlined"
							/>
							<span
								style={{
									fontWeight: 600,
									color: '#1e293b',
									fontSize: '14px'
								}}
							>
								{uploadProgress.total <= 1
									? `Uploading file...`
									: `Uploading ${uploadProgress.completed} of ${uploadProgress.total} files...`}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px'
							}}
						>
							<div
								style={{
									width: '180px',
									height: '6px',
									backgroundColor: '#e2e8f0',
									borderRadius: '3px',
									overflow: 'hidden'
								}}
							>
								<div
									style={{
										width: `${(uploadProgress.completed / Math.max(uploadProgress.total, 1)) * 100}%`,
										height: '100%',
										backgroundColor: '#6366f1',
										transition: 'width 0.2s'
									}}
								></div>
							</div>
							<Tooltip
								title="Cancel upload"
								arrow
								placement="top"
							>
								<IconButton
									size="small"
									onClick={handleCancelBatchUpload}
									style={{ color: '#64748b' }}
								>
									<i
										className="fa-solid fa-xmark"
										style={{ fontSize: '15px' }}
									></i>
								</IconButton>
							</Tooltip>
						</div>
					</div>

					{/* Inner Embedded Warning Box for Active Large File(s) */}
					{Object.entries(largeFileUploads).map(
						([fileName, progress]) => (
							<div
								key={fileName}
								style={{
									backgroundColor: '#fffbeb',
									border: '1px solid #fde68a',
									borderRadius: '6px',
									padding: '10px 14px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between'
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '2px'
									}}
								>
									<span
										style={{
											fontWeight: 600,
											color: '#92400e',
											fontSize: '13px'
										}}
									>
										Uploading large file "{fileName}"
									</span>
									<span
										style={{
											fontWeight: 400,
											color: '#b45309',
											fontSize: '12px'
										}}
									>
										A local copy is being safely cached to
										allow resuming. Please do not close this
										tab!
									</span>
								</div>
								<div
									style={{
										width: '140px',
										height: '4px',
										backgroundColor: '#fde68a',
										borderRadius: '2px',
										overflow: 'hidden'
									}}
								>
									<div
										style={{
											width: `${(progress.completed / Math.max(progress.total, 1)) * 100}%`,
											height: '100%',
											backgroundColor: '#d97706',
											transition: 'width 0.2s'
										}}
									></div>
								</div>
							</div>
						)
					)}
				</div>
			)}
			{/* Unified Copy Progress Card */}
			{isCopyingBatch && (
				<div
					style={{
						backgroundColor: '#f8fafc',
						border: '1px solid #cbd5e1',
						borderRadius: '8px',
						padding: '12px 16px',
						marginBottom: '16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px'
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px'
							}}
						>
							<Loader
								size={20}
								color="primary"
								variant="outlined"
							/>
							<span
								style={{
									fontWeight: 600,
									color: '#1e293b',
									fontSize: '14px'
								}}
							>
								{copyProgress.title ||
									`Copying ${copyProgress.completed} of ${copyProgress.total} files...`}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px'
							}}
						>
							<div
								style={{
									width: '180px',
									height: '6px',
									backgroundColor: '#e2e8f0',
									borderRadius: '3px',
									overflow: 'hidden'
								}}
							>
								<div
									style={{
										width: `${(copyProgress.completed / Math.max(copyProgress.total, 1)) * 100}%`,
										height: '100%',
										backgroundColor: '#6366f1',
										transition: 'width 0.2s'
									}}
								></div>
							</div>
						</div>
					</div>
				</div>
			)}
			{/* Unified Move Progress Card */}
			{!cancellingMoveInfo && isMoving && (
				<div
					style={{
						backgroundColor: '#f8fafc',
						border: '1px solid #cbd5e1',
						borderRadius: '8px',
						padding: '12px 16px',
						marginBottom: '16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '12px'
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between'
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '12px'
							}}
						>
							<Loader
								size={20}
								color="primary"
								variant="outlined"
							/>
							<span
								style={{
									fontWeight: 600,
									color: '#1e293b',
									fontSize: '14px'
								}}
							>
								{moveProgress.total <= 1
									? `Moving item...`
									: `Moving ${moveProgress.completed} of ${moveProgress.total} items...`}
							</span>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '16px'
							}}
						>
							<div
								style={{
									width: '180px',
									height: '6px',
									backgroundColor: '#e2e8f0',
									borderRadius: '3px',
									overflow: 'hidden'
								}}
							>
								<div
									style={{
										width: `${(moveProgress.completed / Math.max(moveProgress.total, 1)) * 100}%`,
										height: '100%',
										backgroundColor: '#6366f1',
										transition: 'width 0.2s'
									}}
								></div>
							</div>
							<Tooltip title="Cancel move" arrow placement="top">
								<IconButton
									size="small"
									onClick={handleCancelBatchMove}
									style={{ color: '#64748b' }}
								>
									<i
										className="fa-solid fa-xmark"
										style={{ fontSize: '15px' }}
									></i>
								</IconButton>
							</Tooltip>
						</div>
					</div>
				</div>
			)}
			{/* File List Dropzone */}
			<div
				onDragEnter={(e) => {
					if (draggedItem || draggedItems.length > 0) {
						e.preventDefault();
					} else {
						handleDrag(e);
					}
				}}
				onDragLeave={(e) => {
					if (!draggedItem && draggedItems.length === 0) {
						handleDrag(e);
					}
				}}
				onDragOver={(e) => {
					if (draggedItem || draggedItems.length > 0) {
						e.preventDefault();
					} else {
						handleDrag(e);
					}
				}}
				onDrop={(e) => {
					if (draggedItem || draggedItems.length > 0) {
						handleDropOnTargetFolder(e, path);
					} else {
						handleDrop(e);
					}
				}}
				style={{
					flex: 1,
					position: 'relative',
					border: '1px solid #e2e8f0',
					outline:
						draggedItem || draggedItems.length > 0
							? '2px dashed #6366f1'
							: dragActive
								? '2px dashed #6366f1'
								: 'none',
					outlineOffset: '-2px',
					borderRadius: '8px',
					backgroundColor: '#fff',
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					marginBottom: '20px'
				}}
			>
				{(draggedItem || draggedItems.length > 0) && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							zIndex: 15,
							backgroundColor: '#eef2ff',
							color: '#4f46e5',
							borderBottom: '1px dashed #c7d2fe',
							padding: '8px 16px',
							fontSize: '13px',
							fontWeight: 600,
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
							pointerEvents: 'none'
						}}
					>
						<i className="fa-solid fa-arrow-down-to-bracket"></i>
						Release mouse anywhere in this area to move{' '}
						{draggedItems.length > 1
							? `${draggedItems.length} items`
							: 'item'}{' '}
						to current folder ({path === '' ? 'Root' : `/${path}`})
					</div>
				)}
				{/* Global Drag Overlay */}
				{dragActive && (
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(255,255,255,0.9)',
							zIndex: 10,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							color: '#4f46e5'
						}}
					>
						<i
							className="fa-solid fa-cloud-arrow-up"
							style={{ fontSize: '64px', marginBottom: '16px' }}
						></i>
						<h2 style={{ margin: 0 }}>Drop files here to upload</h2>
						<p style={{ marginTop: '8px', color: '#6366f1' }}>
							Upload to{' '}
							{viewMode === 'tree' ? `/${path}` : 'root'}
						</p>
					</div>
				)}

				{isLoading ? (
					<div style={{ flex: 1, overflowY: 'auto' }}>
						{layoutMode === 'grid' ? (
							<div>
								{/* Grid View Top Bar Skeleton */}
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										padding: '10px 16px',
										borderBottom: '1px solid #e2e8f0',
										backgroundColor: '#f8fafc',
										fontSize: '13px'
									}}
								>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '8px'
										}}
									>
										<div
											className="cfs-skeleton-shimmer"
											style={{
												width: '16px',
												height: '16px',
												borderRadius: '4px'
											}}
										/>
										<div
											className="cfs-skeleton-shimmer"
											style={{
												width: '110px',
												height: '14px',
												borderRadius: '4px'
											}}
										/>
									</div>
								</div>

								{/* Grid Items Skeleton Grid */}
								<div
									style={{
										display: 'grid',
										gridTemplateColumns:
											'repeat(auto-fill, minmax(180px, 1fr))',
										gap: '16px',
										padding: '16px'
									}}
								>
									{Array.from({ length: 12 }).map(
										(_, idx) => (
											<div
												key={idx}
												style={{
													border: '1px solid #e2e8f0',
													backgroundColor: '#ffffff',
													borderRadius: '8px',
													padding:
														'10px 12px 12px 12px',
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													position: 'relative'
												}}
											>
												{/* Top Controls Skeleton */}
												<div
													style={{
														width: '100%',
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'space-between',
														marginBottom: '8px'
													}}
												>
													<div
														className="cfs-skeleton-shimmer"
														style={{
															width: '16px',
															height: '16px',
															borderRadius: '4px'
														}}
													/>
													<div
														className="cfs-skeleton-shimmer"
														style={{
															width: '18px',
															height: '18px',
															borderRadius: '50%'
														}}
													/>
												</div>

												{/* Thumbnail Skeleton */}
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '100%',
														height: '76px',
														borderRadius: '6px',
														marginBottom: '10px'
													}}
												/>

												{/* File Name Skeleton */}
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: `${60 + (idx % 3) * 15}%`,
														height: '13px',
														borderRadius: '4px',
														marginBottom: '6px'
													}}
												/>

												{/* Size/Subtitle Skeleton */}
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '40%',
														height: '11px',
														borderRadius: '4px'
													}}
												/>
											</div>
										)
									)}
								</div>
							</div>
						) : (
							<table
								style={{
									width: '100%',
									borderCollapse: 'collapse',
									fontSize: '13px'
								}}
							>
								<thead>
									<tr
										style={{
											borderBottom: '1px solid #e2e8f0',
											backgroundColor: '#f8fafc',
											textAlign: 'left'
										}}
									>
										<th
											style={{
												width: '40px',
												padding: '8px 16px'
											}}
										>
											<div
												className="cfs-skeleton-shimmer"
												style={{
													width: '16px',
													height: '16px',
													borderRadius: '4px'
												}}
											/>
										</th>
										<th
											style={{
												padding: '8px 16px',
												fontWeight: 500,
												color: '#475569',
												width:
													viewMode !== 'tree'
														? '30%'
														: 'auto'
											}}
										>
											Name
										</th>
										{viewMode !== 'tree' && (
											<th
												style={{
													padding: '8px 16px',
													fontWeight: 500,
													color: '#475569',
													width: '30%'
												}}
											>
												Path
											</th>
										)}
										<th
											style={{
												padding: '8px 16px',
												fontWeight: 500,
												color: '#475569',
												width: '100px'
											}}
										>
											Size
										</th>
										<th
											style={{
												padding: '8px 16px',
												fontWeight: 500,
												color: '#475569',
												width: '180px'
											}}
										>
											Last Modified
										</th>
										<th
											style={{
												padding: '8px 16px',
												width: '60px'
											}}
										></th>
									</tr>
								</thead>
								<tbody>
									{Array.from({ length: 8 }).map((_, idx) => (
										<tr
											key={idx}
											style={{
												borderBottom:
													'1px solid #f1f5f9',
												height: '44px'
											}}
										>
											<td style={{ padding: '8px 16px' }}>
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '16px',
														height: '16px',
														borderRadius: '4px'
													}}
												/>
											</td>
											<td style={{ padding: '8px 16px' }}>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '10px'
													}}
												>
													<div
														className="cfs-skeleton-shimmer"
														style={{
															width: '20px',
															height: '20px',
															borderRadius: '4px',
															flexShrink: 0
														}}
													/>
													<div
														className="cfs-skeleton-shimmer"
														style={{
															width: `${40 + (idx % 4) * 15}%`,
															height: '13px',
															borderRadius: '4px'
														}}
													/>
												</div>
											</td>
											{viewMode !== 'tree' && (
												<td
													style={{
														padding: '8px 16px'
													}}
												>
													<div
														className="cfs-skeleton-shimmer"
														style={{
															width: '55%',
															height: '12px',
															borderRadius: '4px'
														}}
													/>
												</td>
											)}
											<td style={{ padding: '8px 16px' }}>
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '45px',
														height: '12px',
														borderRadius: '4px'
													}}
												/>
											</td>
											<td style={{ padding: '8px 16px' }}>
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '110px',
														height: '12px',
														borderRadius: '4px'
													}}
												/>
											</td>
											<td
												style={{
													padding: '8px 16px',
													textAlign: 'right'
												}}
											>
												<div
													className="cfs-skeleton-shimmer"
													style={{
														width: '20px',
														height: '20px',
														borderRadius: '50%',
														marginLeft: 'auto'
													}}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				) : currentViewItems.length === 0 ? (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							height: '100%',
							color: '#64748b'
						}}
					>
						<i
							className="fa-solid fa-folder-open"
							style={{
								fontSize: '48px',
								marginBottom: '16px',
								color: '#cbd5e1'
							}}
						></i>
						<p>
							{files.length === 0
								? 'No files in storage'
								: 'Folder is empty'}
						</p>
						<p style={{ fontSize: '14px' }}>
							Drag and drop files to upload
						</p>
					</div>
				) : (
					<div style={{ overflowY: 'auto', flex: 1 }}>
						{layoutMode === 'grid' ? (
							<div>
								{/* Grid View Top Bar with Select All */}
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										padding: '10px 16px',
										borderBottom: '1px solid #e2e8f0',
										backgroundColor: '#f8fafc',
										fontSize: '13px'
									}}
								>
									<label
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
											cursor: 'pointer',
											fontWeight: 500,
											color: '#334155',
											userSelect: 'none'
										}}
									>
										<input
											type="checkbox"
											style={{
												cursor: 'pointer',
												width: '16px',
												height: '16px',
												accentColor: '#4f46e5'
											}}
											checked={
												currentViewItems.length > 0 &&
												currentViewItems.every((item) =>
													selectedKeys.has(item.key)
												)
											}
											onChange={(e) => {
												const checked =
													e.target.checked;
												const newKeys = new Set(
													selectedKeys
												);
												if (checked) {
													currentViewItems.forEach(
														(item) =>
															newKeys.add(
																item.key
															)
													);
												} else {
													currentViewItems.forEach(
														(item) =>
															newKeys.delete(
																item.key
															)
													);
												}
												setSelectedKeys(newKeys);
											}}
										/>
										Select All ({currentViewItems.length}{' '}
										items)
									</label>
									{selectedKeys.size > 0 && (
										<span
											style={{
												color: '#4f46e5',
												fontWeight: 600
											}}
										>
											{selectedKeys.size} selected
										</span>
									)}
								</div>

								{/* Grid Items */}
								<div
									style={{
										display: 'grid',
										gridTemplateColumns:
											'repeat(auto-fill, minmax(180px, 1fr))',
										gap: '16px',
										padding: '16px'
									}}
								>
									{currentViewItems.map((file) => {
										const isHoveredTarget =
											dragHoverTargetKey === file.key;
										const isOperating = operatingKeys.has(
											file.key
										);
										return (
											<div
												key={file.key}
												draggable
												onDragStart={(e) => {
													e.stopPropagation();
													wasDraggingRef.current = true;
													let itemsToDrag: IFile[] =
														[];
													if (
														selectedKeys.has(
															file.key
														) &&
														selectedKeys.size > 1
													) {
														itemsToDrag =
															currentViewItems.filter(
																(f) =>
																	selectedKeys.has(
																		f.key
																	)
															);
													} else {
														itemsToDrag = [file];
													}
													setDraggedItem(file);
													setDraggedItems(
														itemsToDrag
													);
													e.dataTransfer.setData(
														'text/plain',
														itemsToDrag
															.map((i) => i.key)
															.join(',')
													);
													if (
														itemsToDrag.length > 1
													) {
														const dragBadge =
															document.createElement(
																'div'
															);
														dragBadge.style.position =
															'absolute';
														dragBadge.style.top =
															'-1000px';
														dragBadge.style.backgroundColor =
															'#4f46e5';
														dragBadge.style.color =
															'#ffffff';
														dragBadge.style.padding =
															'6px 14px';
														dragBadge.style.borderRadius =
															'20px';
														dragBadge.style.fontSize =
															'13px';
														dragBadge.style.fontWeight =
															'600';
														dragBadge.style.boxShadow =
															'0 4px 12px rgba(0,0,0,0.2)';
														dragBadge.innerText = `Moving ${itemsToDrag.length} items`;
														document.body.appendChild(
															dragBadge
														);
														e.dataTransfer.setDragImage(
															dragBadge,
															15,
															15
														);
														setTimeout(() => {
															if (
																document.body.contains(
																	dragBadge
																)
															) {
																document.body.removeChild(
																	dragBadge
																);
															}
														}, 0);
													}
												}}
												onDragEnd={() => {
													clearHoverTimer();
													setDraggedItem(null);
													setDraggedItems([]);
													setDragHoverTargetKey(null);
													setTimeout(() => {
														wasDraggingRef.current = false;
													}, 200);
												}}
												onDragOver={(e) => {
													if (file.isFolder)
														handleFolderDragOver(
															e,
															file.key
														);
												}}
												onDragLeave={(e) => {
													if (file.isFolder)
														handleFolderDragLeave(
															e
														);
												}}
												onDrop={(e) => {
													if (file.isFolder)
														handleDropOnTargetFolder(
															e,
															file.key
														);
												}}
												style={{
													border: isHoveredTarget
														? '2px dashed #6366f1'
														: selectedKeys.has(
																	file.key
															  )
															? '2px solid #6366f1'
															: '1px solid #e2e8f0',
													backgroundColor:
														isHoveredTarget
															? '#eeeffe'
															: selectedKeys.has(
																		file.key
																  )
																? '#eeeffe'
																: '#ffffff',
													borderRadius: '8px',
													padding:
														'10px 12px 12px 12px',
													display: 'flex',
													flexDirection: 'column',
													alignItems: 'center',
													textAlign: 'center',
													cursor: 'pointer',
													position: 'relative',
													transition:
														'all 0.15s ease',
													opacity: isOperating
														? 0.7
														: 1,
													userSelect: 'none',
													WebkitUserSelect: 'none'
												}}
												onClick={() => {
													if (
														wasDraggingRef.current ||
														draggedItem
													) {
														return;
													}
													if (file.isFolder) {
														setUrlParam(
															'path',
															file.key
														);
													} else {
														navigate(
															`view/${encodeURIComponent(file.key)}`
														);
													}
												}}
											>
												{/* Top Card Controls: Round Checkbox + 3-Dot Menu */}
												<div
													style={{
														width: '100%',
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'space-between',
														marginBottom: '8px'
													}}
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<input
														type="checkbox"
														checked={selectedKeys.has(
															file.key
														)}
														style={{
															cursor: 'pointer',
															width: '16px',
															height: '16px',
															accentColor:
																'#4f46e5'
														}}
														onChange={(e) => {
															const newValue =
																e.target
																	.checked;
															const newKeys =
																new Set(
																	selectedKeys
																);
															if (newValue) {
																newKeys.add(
																	file.key
																);
															} else {
																newKeys.delete(
																	file.key
																);
															}
															setSelectedKeys(
																newKeys
															);
														}}
													/>
													<IconButton
														size="small"
														style={{
															width: 24,
															height: 24
														}}
														onClick={(e) => {
															e.stopPropagation();
															handleOpenMenu(
																e,
																file
															);
														}}
													>
														<i
															className="fa-solid fa-ellipsis-vertical"
															style={{
																fontSize:
																	'14px',
																color: '#64748b'
															}}
														/>
													</IconButton>
												</div>

												{downloadingKeys.has(
													file.key
												) ? (
													<div
														style={{
															height: '48px',
															marginBottom:
																'12px',
															display: 'flex',
															alignItems:
																'center',
															justifyContent:
																'center'
														}}
													>
														<Loader
															size={36}
															color="primary"
															variant="outlined"
														/>
													</div>
												) : !file.isFolder &&
												  getFileIcon(file.key).type ===
														'image' ? (
													<ImageThumbnail
														file={file as IFile}
														project={project}
														resourceId={resourceId}
													/>
												) : (
													<i
														className={
															file.isFolder
																? 'fa-solid fa-folder'
																: getFileIcon(
																		file.key
																	).className
														}
														style={{
															fontSize: '48px',
															color: file.isFolder
																? '#6366f1'
																: getFileIcon(
																		file.key
																	).color,
															marginBottom: '12px'
														}}
													></i>
												)}
												<span
													style={{
														fontSize: '13px',
														fontWeight: 500,
														color: '#1e293b',
														wordBreak: 'break-all'
													}}
												>
													{file.name}
												</span>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '6px',
														marginTop: '4px'
													}}
												>
													<span
														style={{
															fontSize: '11px',
															color: downloadingKeys.has(
																file.key
															)
																? '#6366f1'
																: '#64748b',
															fontWeight:
																downloadingKeys.has(
																	file.key
																)
																	? 600
																	: 400
														}}
													>
														{downloadingKeys.has(
															file.key
														)
															? 'Downloading...'
															: operatingKeys.has(
																		file.key
																  )
																? 'Processing...'
																: formatBytes(
																		file.size
																	)}
													</span>
													{(() => {
														const bucketAccessRaw =
															(
																bucketEntity?.details as any
															)?.access ||
															((
																bucketEntity?.details as any
															)?.publicReadAccess
																? 'public-read'
																: 'private');
														const isBucketPublic =
															bucketAccessRaw.toLowerCase() ===
																'public' ||
															bucketAccessRaw.toLowerCase() ===
																'public-read';
														const fileAccessRaw = (
															file as any
														).access;
														const isFilePublic =
															fileAccessRaw
																? fileAccessRaw.toLowerCase() ===
																		'public' ||
																	fileAccessRaw.toLowerCase() ===
																		'public-read'
																: isBucketPublic;
														const showBadge =
															Boolean(
																fileAccessRaw
															) &&
															isFilePublic !==
																isBucketPublic;
														if (!showBadge)
															return null;
														const tooltipText =
															isFilePublic
																? 'Explicitly public file in a private storage. It will be accessible through the internet.'
																: "Explicitly private file in a public storage. It won't be accessible through the internet.";
														return (
															<Tooltip
																title={
																	tooltipText
																}
																arrow
																placement="top"
															>
																<span
																	style={{
																		backgroundColor:
																			isFilePublic
																				? '#dcfce7'
																				: '#fef3c7',
																		color: isFilePublic
																			? '#15803d'
																			: '#b45309',
																		border: isFilePublic
																			? '1px solid #bbf7d0'
																			: '1px solid #fde68a',
																		borderRadius:
																			'8px',
																		padding:
																			'1px 6px',
																		fontSize:
																			'10px',
																		fontWeight: 600,
																		userSelect:
																			'none',
																		cursor: 'help'
																	}}
																>
																	{isFilePublic
																		? 'PUBLIC'
																		: 'PRIVATE'}
																</span>
															</Tooltip>
														);
													})()}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						) : (
							<table
								style={{
									width: '100%',
									borderCollapse: 'collapse',
									textAlign: 'left',
									tableLayout: 'fixed'
								}}
							>
								<thead
									style={{
										backgroundColor: '#f8fafc',
										borderBottom: '1px solid #e2e8f0'
									}}
								>
									<tr>
										<th
											style={{
												padding: '6px 16px',
												width: '40px'
											}}
										>
											<input
												type="checkbox"
												style={{
													cursor: 'pointer',
													transform: 'scale(1.1)'
												}}
												checked={
													currentViewItems.length >
														0 &&
													currentViewItems.every(
														(file) =>
															selectedKeys.has(
																file.key
															)
													)
												}
												onChange={(e) => {
													const newValue =
														e.target.checked;
													if (newValue) {
														const newKeys = new Set(
															selectedKeys
														);
														currentViewItems.forEach(
															(file) =>
																newKeys.add(
																	file.key
																)
														);
														setSelectedKeys(
															newKeys
														);
													} else {
														const newKeys = new Set(
															selectedKeys
														);
														currentViewItems.forEach(
															(file) =>
																newKeys.delete(
																	file.key
																)
														);
														setSelectedKeys(
															newKeys
														);
													}
												}}
											/>
										</th>
										<th
											style={{
												padding: '6px 16px',
												fontWeight: 500,
												color: '#475569',
												width:
													viewMode !== 'tree'
														? '30%'
														: 'auto'
											}}
										>
											Name
										</th>
										{viewMode !== 'tree' && (
											<th
												style={{
													padding: '6px 16px',
													fontWeight: 500,
													color: '#475569',
													width: '30%'
												}}
											>
												Path
											</th>
										)}
										<th
											style={{
												padding: '6px 16px',
												fontWeight: 500,
												color: '#475569',
												width: '100px'
											}}
										>
											Size
										</th>
										<th
											style={{
												padding: '6px 16px',
												fontWeight: 500,
												color: '#475569',
												width: '180px'
											}}
										>
											Last Modified
										</th>
										<th
											style={{
												padding: '6px 16px',
												fontWeight: 500,
												color: '#475569',
												textAlign: 'right',
												width: '60px'
											}}
										></th>
									</tr>
								</thead>
								<tbody>
									{currentViewItems.map((file) => {
										const icon = file.isFolder
											? {
													className:
														'fa-solid fa-folder',
													color: '#6366f1'
												}
											: getFileIcon(file.key);
										const isHoveredTarget =
											dragHoverTargetKey === file.key;
										const isOperating = operatingKeys.has(
											file.key
										);
										return (
											<tr
												key={file.key}
												draggable
												onMouseDown={(e) => {
													console.info(
														'[TableDragDebug] row onMouseDown',
														{
															key: file.key,
															targetTag: (
																e.target as HTMLElement
															)?.tagName,
															targetClass: (
																e.target as HTMLElement
															)?.className
														}
													);
												}}
												onDragStart={(e) => {
													console.info(
														'[TableDragDebug] row onDragStart',
														{
															key: file.key,
															targetTag: (
																e.target as HTMLElement
															)?.tagName
														}
													);
													e.stopPropagation();
													wasDraggingRef.current = true;
													let itemsToDrag: IFile[] =
														[];
													if (
														selectedKeys.has(
															file.key
														) &&
														selectedKeys.size > 1
													) {
														itemsToDrag =
															currentViewItems.filter(
																(f) =>
																	selectedKeys.has(
																		f.key
																	)
															);
													} else {
														itemsToDrag = [file];
													}
													setDraggedItem(file);
													setDraggedItems(
														itemsToDrag
													);
													e.dataTransfer.setData(
														'text/plain',
														itemsToDrag
															.map((i) => i.key)
															.join(',')
													);
													if (
														itemsToDrag.length > 1
													) {
														const dragBadge =
															document.createElement(
																'div'
															);
														dragBadge.style.position =
															'absolute';
														dragBadge.style.top =
															'-1000px';
														dragBadge.style.backgroundColor =
															'#4f46e5';
														dragBadge.style.color =
															'#ffffff';
														dragBadge.style.padding =
															'6px 14px';
														dragBadge.style.borderRadius =
															'20px';
														dragBadge.style.fontSize =
															'13px';
														dragBadge.style.fontWeight =
															'600';
														dragBadge.style.boxShadow =
															'0 4px 12px rgba(0,0,0,0.2)';
														dragBadge.innerText = `Moving ${itemsToDrag.length} items`;
														document.body.appendChild(
															dragBadge
														);
														e.dataTransfer.setDragImage(
															dragBadge,
															15,
															15
														);
														setTimeout(() => {
															if (
																document.body.contains(
																	dragBadge
																)
															) {
																document.body.removeChild(
																	dragBadge
																);
															}
														}, 0);
													}
												}}
												onDragEnd={() => {
													console.info(
														'[TableDragDebug] row onDragEnd',
														{ key: file.key }
													);
													clearHoverTimer();
													setDraggedItem(null);
													setDraggedItems([]);
													setDragHoverTargetKey(null);
													setTimeout(() => {
														wasDraggingRef.current = false;
													}, 200);
												}}
												onDragOver={(e) => {
													if (file.isFolder)
														handleFolderDragOver(
															e,
															file.key
														);
												}}
												onDragLeave={(e) => {
													if (file.isFolder)
														handleFolderDragLeave(
															e
														);
												}}
												onDrop={(e) => {
													if (file.isFolder)
														handleDropOnTargetFolder(
															e,
															file.key
														);
												}}
												style={{
													borderBottom:
														'1px solid #f1f5f9',
													cursor: 'pointer',
													userSelect: 'none',
													WebkitUserSelect: 'none',
													backgroundColor:
														isHoveredTarget
															? '#eeeffe'
															: hoveredRowKey ===
																  file.key
																? '#f8fafc'
																: 'transparent',
													outline: isHoveredTarget
														? '2px dashed #6366f1'
														: 'none',
													transition:
														'background-color 0.15s ease',
													height: '50px',
													opacity: isOperating
														? 0.6
														: 1
												}}
												onMouseEnter={() =>
													setHoveredRowKey(file.key)
												}
												onMouseLeave={() =>
													setHoveredRowKey(null)
												}
												onClick={(e) => {
													console.info(
														'[TableDragDebug] row onClick',
														{
															key: file.key,
															wasDragging:
																wasDraggingRef.current,
															hasDraggedItem:
																!!draggedItem,
															targetTag: (
																e.target as HTMLElement
															)?.tagName
														}
													);
													if (
														wasDraggingRef.current ||
														draggedItem
													) {
														return;
													}
													if (file.isFolder) {
														setUrlParam(
															'path',
															file.key
														);
													} else {
														if (
															e.metaKey ||
															e.ctrlKey
														) {
															window.open(
																`${window.location.origin}${window.location.pathname}?viewFile=${encodeURIComponent(file.key)}`,
																'_blank'
															);
														} else {
															navigate(
																`view/${encodeURIComponent(file.key)}`
															);
														}
													}
												}}
											>
												<td
													style={{
														padding: '6px 16px',
														width: '40px'
													}}
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<input
														type="checkbox"
														style={{
															cursor: 'pointer',
															transform:
																'scale(1.1)'
														}}
														checked={selectedKeys.has(
															file.key
														)}
														onChange={(e) => {
															const newValue =
																e.target
																	.checked;
															const newKeys =
																new Set(
																	selectedKeys
																);
															if (newValue) {
																newKeys.add(
																	file.key
																);
															} else {
																newKeys.delete(
																	file.key
																);
															}
															setSelectedKeys(
																newKeys
															);
														}}
													/>
												</td>
												<td
													style={{
														padding: '6px 16px',
														verticalAlign: 'middle'
													}}
												>
													<div
														style={{
															display: 'flex',
															alignItems:
																'center',
															width: '100%',
															overflow: 'hidden'
														}}
													>
														<i
															className={
																icon.className
															}
															style={{
																marginRight:
																	'8px',
																color: icon.color,
																width: '16px',
																textAlign:
																	'center',
																flexShrink: 0
															}}
														></i>
														<span
															style={{
																fontWeight:
																	file.isFolder
																		? 600
																		: 400,
																color: '#1e293b',
																overflow:
																	'hidden',
																textOverflow:
																	'ellipsis',
																whiteSpace:
																	'nowrap',
																flex: 1
															}}
															title={file.name}
														>
															{file.name}
														</span>
														{(() => {
															const bucketAccessRaw =
																(
																	bucketEntity?.details as any
																)?.access ||
																((
																	bucketEntity?.details as any
																)
																	?.publicReadAccess
																	? 'public-read'
																	: 'private');
															const isBucketPublic =
																bucketAccessRaw.toLowerCase() ===
																	'public' ||
																bucketAccessRaw.toLowerCase() ===
																	'public-read';
															const fileAccessRaw =
																(file as any)
																	.access;
															const isFilePublic =
																fileAccessRaw
																	? fileAccessRaw.toLowerCase() ===
																			'public' ||
																		fileAccessRaw.toLowerCase() ===
																			'public-read'
																	: isBucketPublic;
															const showBadge =
																Boolean(
																	fileAccessRaw
																) &&
																isFilePublic !==
																	isBucketPublic;
															if (!showBadge)
																return null;
															const tooltipText =
																isFilePublic
																	? 'Explicitly public file in a private storage. It will be accessible through the internet.'
																	: "Explicitly private file in a public storage. It won't be accessible through the internet.";
															return (
																<Tooltip
																	title={
																		tooltipText
																	}
																	arrow
																	placement="top"
																>
																	<span
																		style={{
																			backgroundColor:
																				isFilePublic
																					? '#dcfce7'
																					: '#fef3c7',
																			color: isFilePublic
																				? '#15803d'
																				: '#b45309',
																			border: isFilePublic
																				? '1px solid #bbf7d0'
																				: '1px solid #fde68a',
																			borderRadius:
																				'10px',
																			padding:
																				'2px 8px',
																			fontSize:
																				'11px',
																			fontWeight: 600,
																			marginLeft:
																				'8px',
																			flexShrink: 0,
																			userSelect:
																				'none',
																			cursor: 'help'
																		}}
																	>
																		{isFilePublic
																			? 'PUBLIC'
																			: 'PRIVATE'}
																	</span>
																</Tooltip>
															);
														})()}
														<Tooltip
															title="Copy name"
															arrow
															placement="top"
														>
															<button
																onClick={(
																	e
																) => {
																	e.stopPropagation();
																	navigator.clipboard.writeText(
																		file.name
																	);
																	popupNotification(
																		{
																			text: 'Copied!',
																			color: 'primary'
																		}
																	);
																}}
																style={{
																	background:
																		'none',
																	border: 'none',
																	color: '#cbd5e1',
																	cursor: 'pointer',
																	marginLeft:
																		'8px',
																	opacity: 0.5,
																	flexShrink: 0
																}}
															>
																<i className="fa-regular fa-copy"></i>
															</button>
														</Tooltip>
													</div>
												</td>
												{viewMode !== 'tree' && (
													<td
														style={{
															padding: '6px 16px',
															color: '#64748b',
															fontSize: '13px',
															verticalAlign:
																'middle',
															userSelect: 'none',
															WebkitUserSelect:
																'none'
														}}
													>
														<div
															style={{
																display: 'flex',
																alignItems:
																	'center',
																width: '100%',
																overflow:
																	'hidden'
															}}
														>
															<span
																style={{
																	overflow:
																		'hidden',
																	textOverflow:
																		'ellipsis',
																	whiteSpace:
																		'nowrap',
																	flex: 1
																}}
																title={
																	file.key.substring(
																		0,
																		file.key.lastIndexOf(
																			'/'
																		)
																	) || '/'
																}
															>
																{file.key.substring(
																	0,
																	file.key.lastIndexOf(
																		'/'
																	)
																) || '/'}
															</span>
															<Tooltip
																title="Copy path"
																arrow
																placement="top"
															>
																<button
																	onClick={(
																		e
																	) => {
																		e.stopPropagation();
																		navigator.clipboard.writeText(
																			file.key
																		);
																		popupNotification(
																			{
																				text: 'Copied!',
																				color: 'primary'
																			}
																		);
																	}}
																	style={{
																		background:
																			'none',
																		border: 'none',
																		color: '#cbd5e1',
																		cursor: 'pointer',
																		marginLeft:
																			'8px',
																		opacity: 0.5,
																		flexShrink: 0
																	}}
																>
																	<i className="fa-regular fa-copy"></i>
																</button>
															</Tooltip>
														</div>
													</td>
												)}
												<td
													style={{
														padding: '6px 16px',
														color: '#64748b',
														userSelect: 'none',
														WebkitUserSelect: 'none'
													}}
												>
													{formatBytes(file.size)}
												</td>
												<td
													style={{
														padding: '6px 16px',
														color: '#64748b',
														userSelect: 'none',
														WebkitUserSelect: 'none'
													}}
												>
													{new Date(
														file.lastModified
													).toLocaleString()}
												</td>
												<td
													style={{
														padding: '6px 12px',
														textAlign: 'right',
														verticalAlign: 'middle',
														whiteSpace: 'nowrap',
														width: '60px'
													}}
													onClick={(e) =>
														e.stopPropagation()
													}
												>
													<div
														style={{
															display:
																'inline-flex',
															alignItems:
																'center',
															justifyContent:
																'flex-end',
															gap: '8px'
														}}
													>
														{downloadingKeys.has(
															file.key
														) && (
															<Tooltip
																title="Downloading..."
																arrow
																placement="top"
															>
																<div
																	style={{
																		display:
																			'flex',
																		alignItems:
																			'center'
																	}}
																>
																	<Loader
																		size={
																			16
																		}
																		color="primary"
																		variant="outlined"
																	/>
																</div>
															</Tooltip>
														)}
														<Tooltip
															title="More actions"
															arrow
															placement="top"
														>
															<IconButton
																size="small"
																style={{
																	width: 26
																}}
																onClick={(e) =>
																	handleOpenMenu(
																		e,
																		file
																	)
																}
															>
																<i
																	className="fa-solid fa-ellipsis-vertical"
																	style={{
																		fontSize:
																			'16px'
																	}}
																/>
															</IconButton>
														</Tooltip>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						)}
					</div>
				)}
			</div>
			{isFolderDialogOpen && (
				<SmallDialog
					onClose={() => setIsFolderDialogOpen(false)}
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							flex: 1
						}}
					>
						<h2
							style={{
								fontSize: '18px',
								fontWeight: 600,
								color: '#1e293b',
								margin: 0,
								paddingBottom: '8px',
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<div
								style={{
									background: '#e0e7ff',
									color: '#4f46e5',
									width: '32px',
									height: '32px',
									borderRadius: '8px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '16px'
								}}
							>
								<i className="fa-solid fa-folder-plus"></i>
							</div>
							Create New Folder
						</h2>
						<label
							style={{
								fontSize: '14px',
								fontWeight: 500,
								color: '#374151',
								marginTop: '12px'
							}}
						>
							Folder Name
						</label>
						<input
							type="text"
							value={newFolderName}
							onChange={(e) => setNewFolderName(e.target.value)}
							placeholder="e.g. src/assets/images"
							style={{
								padding: '10px 14px',
								border: folderValidationError
									? '2px solid #ef4444'
									: '2px solid #e2e8f0',
								borderRadius: '8px',
								fontSize: '15px',
								outline: 'none',
								transition: 'border-color 0.2s ease'
							}}
							onFocus={(e) =>
								(e.target.style.borderColor =
									folderValidationError
										? '#ef4444'
										: '#4f46e5')
							}
							onBlur={(e) =>
								(e.target.style.borderColor =
									folderValidationError
										? '#ef4444'
										: '#e2e8f0')
							}
							autoFocus
							onKeyDown={(e) => {
								if (
									e.key === 'Enter' &&
									!folderValidationError &&
									newFolderName.trim()
								) {
									handleCreateFolder();
								}
							}}
						/>
						{folderValidationError && (
							<div
								style={{
									color: '#ef4444',
									fontSize: '13px',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									marginTop: '2px'
								}}
							>
								<i className="fa-solid fa-triangle-exclamation" />
								{folderValidationError}
							</div>
						)}
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								marginTop: 'auto'
							}}
						>
							<Button
								variant="outlined"
								onClick={() => {
									setIsFolderDialogOpen(false);
									setNewFolderName('');
								}}
							>
								Cancel
							</Button>
							<Button
								color="primary"
								disabled={
									!newFolderName.trim() ||
									!!folderValidationError
								}
								onClick={handleCreateFolder}
							>
								Create
							</Button>
						</div>
					</div>
				</SmallDialog>
			)}
			{deleteConfirmFile && (
				<SmallDialog
					onClose={() => setDeleteConfirmFile(null)}
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							flex: 1
						}}
					>
						<h2
							style={{
								fontSize: '18px',
								fontWeight: 600,
								color: '#1e293b',
								margin: 0,
								paddingBottom: '8px',
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<div
								style={{
									background: '#fee2e2',
									color: '#ef4444',
									width: '32px',
									height: '32px',
									borderRadius: '8px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '16px'
								}}
							>
								<i className="fa-solid fa-trash-can"></i>
							</div>
							Delete Item
						</h2>
						<p
							style={{
								margin: 0,
								fontSize: '15px',
								color: '#475569',
								lineHeight: '1.5',
								marginTop: '8px'
							}}
						>
							Are you sure you want to delete{' '}
							<strong style={{ color: '#0f172a' }}>
								{deleteConfirmFile.key}
							</strong>
							?
							<br />
							<span
								style={{
									color: '#ef4444',
									fontSize: '13px',
									marginTop: '4px',
									display: 'inline-block'
								}}
							>
								This action cannot be undone.
							</span>
						</p>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								marginTop: 'auto'
							}}
						>
							<Button
								variant="outlined"
								onClick={() => setDeleteConfirmFile(null)}
							>
								Cancel
							</Button>
							<Button
								color="error"
								onClick={() => {
									handleDelete(deleteConfirmFile.key);
									setDeleteConfirmFile(null);
								}}
							>
								Delete
							</Button>
						</div>
					</div>
				</SmallDialog>
			)}
			{isMoveConflictModalOpen && pendingMove && (
				<SmallDialog
					onClose={handleCancelMove}
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							flex: 1
						}}
					>
						<h2
							style={{
								fontSize: '18px',
								fontWeight: 600,
								color: '#1e293b',
								margin: 0,
								paddingBottom: '8px',
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<div
								style={{
									background: '#fef3c7',
									color: '#d97706',
									width: '32px',
									height: '32px',
									borderRadius: '8px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '16px'
								}}
							>
								<i className="fa-solid fa-triangle-exclamation"></i>
							</div>
							Move Conflict
						</h2>
						<p
							style={{
								margin: 0,
								fontSize: '15px',
								color: '#475569',
								lineHeight: '1.5',
								marginTop: '8px'
							}}
						>
							One or more items already exist in{' '}
							<strong style={{ color: '#0f172a' }}>
								/{pendingMove.destinationFolderPath}
							</strong>
							. Moving them will overwrite existing files. Do you
							wish to continue?
						</p>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								marginTop: 'auto'
							}}
						>
							<Button
								variant="outlined"
								onClick={handleCancelMove}
							>
								Cancel
							</Button>
							<Button
								color="primary"
								onClick={() => handleConfirmMove(true)}
							>
								Overwrite
							</Button>
						</div>
					</div>
				</SmallDialog>
			)}
			{isBulkDeleteDialogOpen && (
				<SmallDialog
					onClose={() => setIsBulkDeleteDialogOpen(false)}
					style={{ display: 'flex', flexDirection: 'column' }}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '8px',
							flex: 1
						}}
					>
						<h2
							style={{
								fontSize: '18px',
								fontWeight: 600,
								color: '#1e293b',
								margin: 0,
								paddingBottom: '8px',
								display: 'flex',
								alignItems: 'center',
								gap: '10px'
							}}
						>
							<div
								style={{
									background: '#fee2e2',
									color: '#ef4444',
									width: '32px',
									height: '32px',
									borderRadius: '8px',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: '16px'
								}}
							>
								<i className="fa-solid fa-trash-can"></i>
							</div>
							Delete Multiple Items
						</h2>
						<p
							style={{
								margin: 0,
								fontSize: '15px',
								color: '#475569',
								lineHeight: '1.5',
								marginTop: '8px'
							}}
						>
							Are you sure you want to delete{' '}
							<strong style={{ color: '#0f172a' }}>
								{expandedSelectedFiles.length} files
							</strong>
							?
							<br />
							<span
								style={{
									color: '#ef4444',
									fontSize: '13px',
									marginTop: '4px',
									display: 'inline-block'
								}}
							>
								This action cannot be undone.
							</span>
						</p>
						<div
							style={{
								display: 'flex',
								justifyContent: 'flex-end',
								gap: '12px',
								marginTop: 'auto'
							}}
						>
							<Button
								variant="outlined"
								onClick={() => setIsBulkDeleteDialogOpen(false)}
							>
								Cancel
							</Button>
							<Button
								color="error"
								onClick={() => confirmBulkDelete()}
							>
								Delete
							</Button>
						</div>
					</div>
				</SmallDialog>
			)}{' '}
			{isRenameDialogOpen &&
				renameTargetFile && {
					...(() => {
						const isFolder =
							(renameTargetFile as any).isFolder ||
							renameTargetFile.key.endsWith('/');
						const cleanKey =
							isFolder && renameTargetFile.key.endsWith('/')
								? renameTargetFile.key.slice(0, -1)
								: renameTargetFile.key;
						const lastSlash = cleanKey.lastIndexOf('/');
						const parentPath =
							lastSlash !== -1
								? cleanKey.substring(0, lastSlash + 1)
								: '';
						const trimmedNewName = renameNewName.trim();
						const proposedKey =
							parentPath + trimmedNewName + (isFolder ? '/' : '');

						const proposedKeyPrefix =
							parentPath + trimmedNewName + '/';
						const proposedFileKey = parentPath + trimmedNewName;

						const isDuplicate =
							trimmedNewName !== '' &&
							proposedKey !== renameTargetFile.key &&
							files.some((f) => {
								if (
									f.key === cleanKey ||
									f.key === cleanKey + '/' ||
									f.key.startsWith(cleanKey + '/')
								) {
									return false;
								}
								if (
									f.key === proposedFileKey ||
									f.key === proposedKeyPrefix
								) {
									return true;
								}
								if (f.key.startsWith(proposedKeyPrefix)) {
									return true;
								}
								return false;
							});

						const isInvalid =
							!trimmedNewName ||
							trimmedNewName.includes('/') ||
							isDuplicate;

						return (
							<SmallDialog
								onClose={() => setIsRenameDialogOpen(false)}
								style={{
									display: 'flex',
									flexDirection: 'column'
								}}
							>
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '12px',
										flex: 1,
										minHeight: '200px',
										justifyContent: 'space-between'
									}}
								>
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											gap: '8px'
										}}
									>
										<h2
											style={{
												fontSize: '18px',
												fontWeight: 600,
												color: '#1e293b',
												margin: 0,
												paddingBottom: '8px',
												display: 'flex',
												alignItems: 'center',
												gap: '10px'
											}}
										>
											<div
												style={{
													background: '#e0e7ff',
													color: '#4f46e5',
													width: '32px',
													height: '32px',
													borderRadius: '8px',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													fontSize: '16px'
												}}
											>
												<i className="fa-solid fa-pen-to-square"></i>
											</div>
											Rename{' '}
											{isFolder ? 'Folder' : 'File'}
										</h2>
										<label
											style={{
												fontSize: '14px',
												fontWeight: 500,
												color: '#374151',
												marginTop: '12px'
											}}
										>
											Name
										</label>
										<input
											type="text"
											value={renameNewName}
											onChange={(e) =>
												setRenameNewName(e.target.value)
											}
											style={{
												padding: '10px 14px',
												border: isDuplicate
													? '2px solid #ef4444'
													: '2px solid #e2e8f0',
												borderRadius: '8px',
												fontSize: '15px',
												outline: 'none',
												transition:
													'border-color 0.2s ease'
											}}
											onFocus={(e) =>
												(e.target.style.borderColor =
													isDuplicate
														? '#ef4444'
														: '#4f46e5')
											}
											onBlur={(e) =>
												(e.target.style.borderColor =
													isDuplicate
														? '#ef4444'
														: '#e2e8f0')
											}
											autoFocus
											onKeyDown={(e) => {
												if (
													e.key === 'Enter' &&
													!isInvalid
												)
													handleRenameSubmit();
											}}
										/>
										{isDuplicate && (
											<span
												style={{
													color: '#ef4444',
													fontSize: '13px',
													marginTop: '4px',
													display: 'flex',
													alignItems: 'center',
													gap: '6px'
												}}
											>
												<i className="fa-solid fa-triangle-exclamation"></i>
												An item with this name already
												exists in this location
											</span>
										)}
									</div>
									<div
										style={{
											display: 'flex',
											justifyContent: 'flex-end',
											gap: '12px',
											marginTop: '24px'
										}}
									>
										<Button
											variant="outlined"
											onClick={() =>
												setIsRenameDialogOpen(false)
											}
										>
											Cancel
										</Button>
										<Button
											color="primary"
											loading={isRenaming}
											disabled={isInvalid || isRenaming}
											onClick={handleRenameSubmit}
										>
											Rename
										</Button>
									</div>
								</div>
							</SmallDialog>
						);
					})()
				}}
			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={() => handleCloseMenu()}
				onClick={(e) => e.stopPropagation()}
			>
				{menuFile && (
					<MenuItem
						disabled={downloadingKeys.has(menuFile.key)}
						onClick={(e) => {
							e.stopPropagation();
							handleDownload(menuFile);
							handleCloseMenu();
						}}
					>
						{downloadingKeys.has(menuFile.key) ? (
							<div
								style={{
									width: 24,
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<Loader
									size={16}
									color="primary"
									variant="outlined"
								/>
							</div>
						) : (
							<i
								className="fa-light fa-download"
								style={{ width: 24 }}
							></i>
						)}
						{downloadingKeys.has(menuFile.key)
							? 'Downloading...'
							: 'Download'}
					</MenuItem>
				)}
				{menuFile && (
					<MenuItem
						disabled={operatingKeys.has(menuFile.key)}
						onClick={(e) => {
							e.stopPropagation();
							handleCopyItem(menuFile);
							handleCloseMenu();
						}}
					>
						<i
							className="fa-light fa-copy"
							style={{ width: 24 }}
						></i>
						Clone
					</MenuItem>
				)}
				{menuFile && (
					<MenuItem
						disabled={operatingKeys.has(menuFile.key)}
						onClick={(e) => {
							e.stopPropagation();
							handleOpenRenameDialog(menuFile);
							handleCloseMenu();
						}}
					>
						<i
							className="fa-light fa-pen-to-square"
							style={{ width: 24 }}
						></i>
						Rename
					</MenuItem>
				)}
				{menuFile &&
					(() => {
						const bucketAccessRaw =
							(bucketEntity?.details as any)?.access ||
							((bucketEntity?.details as any)?.publicReadAccess
								? 'public-read'
								: 'private');

						const isBucketPublic =
							bucketAccessRaw.toLowerCase() === 'public' ||
							bucketAccessRaw.toLowerCase() === 'public-read';

						const fileAccessRaw = (menuFile as any).access;
						const isMenuFilePublic = fileAccessRaw
							? fileAccessRaw.toLowerCase() === 'public' ||
								fileAccessRaw.toLowerCase() === 'public-read'
							: isBucketPublic;

						const labelText = isMenuFilePublic
							? 'Make private'
							: 'Make public';
						const tooltipText = isMenuFilePublic
							? 'Restricts access so only authenticated users with project permissions can access or download this item.'
							: 'Grants public read access to this item over the internet, allowing anyone with the direct URL to view or download it.';

						return (
							<Tooltip title={tooltipText} arrow placement="left">
								<span>
									<MenuItem
										disabled={operatingKeys.has(
											menuFile.key
										)}
										onClick={(e) => {
											e.stopPropagation();
											handleToggleFileAccess(menuFile);
											handleCloseMenu();
										}}
									>
										<i
											className={`fa-light ${isMenuFilePublic ? 'fa-lock' : 'fa-globe'}`}
											style={{ width: 24 }}
										></i>
										{labelText}
									</MenuItem>
								</span>
							</Tooltip>
						);
					})()}
				<MenuItem
					onClick={(e) => {
						e.stopPropagation();
						if (menuFile) setDeleteConfirmFile(menuFile);
						handleCloseMenu();
					}}
					sx={{ color: 'error.main' }}
				>
					<i className="fa-light fa-trash" style={{ width: 24 }}></i>
					Delete
				</MenuItem>
			</Menu>
			<Popover
				open={Boolean(filterAnchorEl)}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left'
				}}
				slotProps={{
					paper: {
						style: { width: 320, maxHeight: '60vh', padding: 0 }
					}
				}}
			>
				<Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
					<Typography
						variant="subtitle2"
						fontWeight="bold"
						sx={{ mb: 1 }}
					>
						Access Permission
					</Typography>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<Button
							size="small"
							variant={!accessFilter ? 'contained' : 'outlined'}
							color={!accessFilter ? 'primary' : 'inherit'}
							onClick={() => setUrlParam('access', '')}
							sx={{
								textTransform: 'none',
								borderRadius: '8px',
								flex: 1,
								minWidth: 0
							}}
						>
							All
						</Button>
						<Button
							size="small"
							variant={
								accessFilter === 'public'
									? 'contained'
									: 'outlined'
							}
							color={
								accessFilter === 'public'
									? 'success'
									: 'inherit'
							}
							startIcon={
								<i
									className="fa-solid fa-globe"
									style={{ fontSize: 12 }}
								></i>
							}
							onClick={() => setUrlParam('access', 'public')}
							sx={{
								textTransform: 'none',
								borderRadius: '8px',
								flex: 1,
								minWidth: 0
							}}
						>
							Public
						</Button>
						<Button
							size="small"
							variant={
								accessFilter === 'private'
									? 'contained'
									: 'outlined'
							}
							color={
								accessFilter === 'private'
									? 'warning'
									: 'inherit'
							}
							startIcon={
								<i
									className="fa-solid fa-lock"
									style={{ fontSize: 12 }}
								></i>
							}
							onClick={() => setUrlParam('access', 'private')}
							sx={{
								textTransform: 'none',
								borderRadius: '8px',
								flex: 1,
								minWidth: 0
							}}
						>
							Private
						</Button>
					</Box>
				</Box>
				<Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
					<Typography variant="subtitle2" fontWeight="bold">
						File Types
					</Typography>
					<Box sx={{ mt: 1 }}>
						{FILE_CATEGORIES.map((category) => {
							const isAllSelected = category.extensions.every(
								(ext) => typeFilter.includes(ext)
							);
							const isSomeSelected = category.extensions.some(
								(ext) => typeFilter.includes(ext)
							);
							return (
								<Accordion
									key={category.name}
									disableGutters
									elevation={0}
									square
									sx={{
										'&:before': { display: 'none' },
										borderBottom: '1px solid #f1f5f9'
									}}
								>
									<AccordionSummary
										expandIcon={
											<i
												className="fa-solid fa-chevron-down"
												style={{ fontSize: '12px' }}
											/>
										}
										sx={{
											px: 0,
											minHeight: 36,
											'& .MuiAccordionSummary-content': {
												m: 0
											}
										}}
									>
										<FormControlLabel
											onClick={(e) => e.stopPropagation()}
											onFocus={(e) => e.stopPropagation()}
											control={
												<Checkbox
													size="small"
													checked={isAllSelected}
													indeterminate={
														isSomeSelected &&
														!isAllSelected
													}
													onChange={(e) => {
														const newFilters =
															new Set(typeFilter);
														if (e.target.checked) {
															category.extensions.forEach(
																(ext) =>
																	newFilters.add(
																		ext
																	)
															);
														} else {
															category.extensions.forEach(
																(ext) =>
																	newFilters.delete(
																		ext
																	)
															);
														}
														setUrlParam(
															'type',
															Array.from(
																newFilters
															).join(',')
														);
													}}
												/>
											}
											label={
												<Typography variant="body2">
													{category.name}
												</Typography>
											}
										/>
									</AccordionSummary>
									<AccordionDetails
										sx={{
											pt: 0,
											pb: 1,
											pl: 4,
											display: 'flex',
											flexWrap: 'wrap',
											gap: 1
										}}
									>
										{category.extensions.map((ext) => (
											<FormControlLabel
												key={ext}
												control={
													<Checkbox
														size="small"
														checked={typeFilter.includes(
															ext
														)}
														onChange={(e) => {
															const newFilters =
																new Set(
																	typeFilter
																);
															if (
																e.target.checked
															) {
																newFilters.add(
																	ext
																);
															} else {
																newFilters.delete(
																	ext
																);
															}
															setUrlParam(
																'type',
																Array.from(
																	newFilters
																).join(',')
															);
														}}
													/>
												}
												label={
													<Typography variant="caption">
														{ext}
													</Typography>
												}
												sx={{ m: 0 }}
											/>
										))}
									</AccordionDetails>
								</Accordion>
							);
						})}
					</Box>
				</Box>
				<Box sx={{ p: 2 }}>
					<Typography
						variant="subtitle2"
						fontWeight="bold"
						sx={{ mb: 1 }}
					>
						Last Modified
					</Typography>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<DateTimePicker
							label="From"
							value={dateFrom ? dayjs(dateFrom) : null}
							onChange={(newValue) =>
								setUrlParam(
									'dateFrom',
									newValue ? newValue.toISOString() : ''
								)
							}
							slotProps={{
								textField: {
									size: 'small',
									fullWidth: true
								}
							}}
						/>
						<DateTimePicker
							label="To"
							value={dateTo ? dayjs(dateTo) : null}
							onChange={(newValue) =>
								setUrlParam(
									'dateTo',
									newValue ? newValue.toISOString() : ''
								)
							}
							slotProps={{
								textField: {
									size: 'small',
									fullWidth: true
								}
							}}
						/>
					</Box>
				</Box>
			</Popover>
		</div>
	);
};

export default CloudFileSystemBucketExplorer;
